import React, { useState, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import {
  Search,
  Users,
  LayoutGrid,
  Save,
  Download,
  Copy,
  ChevronLeft,
  ChevronRight,
  StickyNote,
  Mail,
  Pin,
  PinOff,
  UserCheck,
  UserX,
  Star,
  Trash2,
  FileDown
} from 'lucide-react';
import { apiClient, api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import './Sitzordnung.css';
import {
  Button,
  EmptyState,
  ListToolbar,
  Modal,
  Tile,
  TileContent,
  TileDescription,
  TileFooter,
  TileHeader,
  TileTitle
} from '../components/ui';
import MemoModal from '../components/MemoModal';
import EmailModal from '../components/EmailModal';
import EmailCredentialModal from '../components/EmailCredentialModal';
import PersonalDataRequiredModal from '../components/PersonalDataRequiredModal';
import AbsenceToggle from '../components/AbsenceToggle';
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';
import { fetchActiveAbsences } from '../lib/temporaryAbsencesApi';

interface Student {
  ID: string;
  sophomorixUnid: string;
  Name: string;
  Vorname: string;
  Klasse: string;
  Memo?: string;
}

interface GridPosition {
  studentId: string | null;
  position: number;
  pinned?: boolean;
}

interface Sitzplan {
  id: number;
  name: string;
  beschreibung?: string;
  klasse?: string;
  context_type?: 'class' | 'course' | 'group' | null;
  context_value?: string | null;
  context_label?: string | null;
  grid_rows?: number;
  grid_cols?: number;
  schueler_anzahl?: number;
  created_at?: string;
  updated_at?: string;
  parent_id?: number;
  parent_name?: string;
  is_favorite?: boolean;
  is_public?: boolean;
  ersteller_id?: number;
  ersteller_name?: string;
}

interface SitzplanPosition {
  grid_row: number;
  grid_col: number;
  schueler_id: number;
  vorname: string;
  name: string;
  klasse: string;
  foto?: string;
  memo?: string;
  pinned?: boolean;
}

interface LernbueroSession {
  SessionID: number;
}

const STUDENT_ROW_HEIGHT = 64; // Approximate row height in the list
const VIRTUAL_BUFFER = 5; // Extra rows above/below viewport to preload photos
type SitzplanContextType = 'class' | 'course' | 'group';

const Sitzordnung: React.FC = () => {
  // Auth
  const { user } = useAuth();
  
  // Session and attendance
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [attendanceStatus, setAttendanceStatus] = useState<Record<string, 'present' | 'absent' | 'excused' | null>>({});
  const [activeAbsencesMap, setActiveAbsencesMap] = useState<Record<string, { id: number; started_at: string; reason?: string }>>({});
  
  // Helper functions for permissions
  const isOwner = (sitzplan: Sitzplan) => {
    return user?.username && sitzplan.ersteller_name === user.username;
  };
  
  const canEdit = (sitzplan: Sitzplan) => {
    return isOwner(sitzplan);
  };
  
  const canDelete = (sitzplan: Sitzplan) => {
    return isOwner(sitzplan);
  };
  
  const canView = (sitzplan: Sitzplan) => {
    return sitzplan.is_public || isOwner(sitzplan);
  };
  
  // View State
  const [currentView, setCurrentView] = useState<'list' | 'view' | 'edit'>('list');
  const [previousView, setPreviousView] = useState<'list' | 'view' | 'edit'>('list');
  
  // Schüler aus der Datenbank
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  // Schüler aus dem aktuellen Sitzplan (mit Fotos)
  const [sitzplanStudents, setSitzplanStudents] = useState<Student[]>([]);
  // Schülerfotos mit LRU Cache (wie in Students.tsx)
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({});
  const [photoCacheOrder, setPhotoCacheOrder] = useState<string[]>([]);
  const MAX_CACHE_SIZE = 100; // Maximum number of photos to keep in memory
  const [classes, setClasses] = useState<string[]>(['alle']);
  const [courses, setCourses] = useState<Array<{ id: number; name: string }>>([]);
  const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('alle');
  const [selectedContextType, setSelectedContextType] = useState<SitzplanContextType>('class');
  const [contextStudentIds, setContextStudentIds] = useState<Set<string> | null>(null);
  
  // Sitzplan-Management
  const [sitzplaene, setSitzplaene] = useState<Sitzplan[]>([]);
  const [currentSitzplan, setCurrentSitzplan] = useState<Sitzplan | null>(null);
  
  // Liste-Filter
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [listSelectedKlasse, setListSelectedKlasse] = useState('all');
  const [listCurrentPage, setListCurrentPage] = useState(1);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listSortBy, setListSortBy] = useState('updated_at');
  const [listSortOrder, setListSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  // Grid-Management
  const [gridRows, setGridRows] = useState(8);
  const [gridCols, setGridCols] = useState(8);
  const [gridPositions, setGridPositions] = useState<GridPosition[]>([]);
  
  // Drag & Drop
  const [draggingId, setDraggingId] = useState<string | null>(null);
  
  // Multiselect
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  // Pinned (fixierte) Schüler für Optimierung
  const [pinnedStudents, setPinnedStudents] = useState<Set<string>>(new Set());
  
  // Lernpartnerschaften Modal
  const [showPartnershipModal, setShowPartnershipModal] = useState(false);
  const [partnershipGewichtung, setPartnershipGewichtung] = useState(50);
  const [partnershipHalbwertszeit, setPartnershipHalbwertszeit] = useState(30);
  
   // Lernpartnerschaften Visualisierung
   const [partnerships, setPartnerships] = useState<any[]>([]);
  
  // Read-only Toggle
  const [isReadOnly, setIsReadOnly] = useState(true); // Default aktiv
  
  // Favorites
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Virtual scrolling helpers for student list
  const [listScrollTop, setListScrollTop] = useState(0);
  const [listContainerHeight, setListContainerHeight] = useState(0);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const handleListScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setListScrollTop(event.currentTarget.scrollTop);
  }, []);

  // LRU Cache helper functions (from Students.tsx)
  const updatePhotoCache = useCallback((studentId: string, photo: string | null) => {
    setStudentPhotos(prev => {
      const newPhotos = { ...prev, [studentId]: photo };
      
      // Update cache order
      setPhotoCacheOrder(prevOrder => {
        const newOrder = prevOrder.filter(id => id !== studentId);
        newOrder.push(studentId);
        
        // Remove oldest entries if cache is too large
        if (newOrder.length > MAX_CACHE_SIZE) {
          const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
          toRemove.forEach(id => delete newPhotos[id]);
        }
        
        return newOrder;
      });
      
      return newPhotos;
    });
  }, [MAX_CACHE_SIZE]);

  // Modals
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedStudentForMemo, setSelectedStudentForMemo] = useState<Student | null>(null);
  const [selectedStudentForEmail, setSelectedStudentForEmail] = useState<Student | null>(null);
  const [showEmailCredentialModal, setShowEmailCredentialModal] = useState(false);
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);
  const [pendingEmailStudent, setPendingEmailStudent] = useState<Student | null>(null);

  // Daten laden - OPTIMIERT: Nur Sitzpläne für Übersicht
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // NUR Sitzpläne laden für Übersicht - BLAZING FAST!
        await loadSitzplaeneList();
        
        // Andere Daten werden nur geladen wenn benötigt (lazy loading)
        
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Verbindungsfehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Keep the Lernbüro session in sync so temporary-absence actions stay available.
  const syncCurrentSessionId = useCallback(async () => {
    const savedSessionId = sessionStorage.getItem('lernbuero-selected-session-id');
    const parsedSavedSessionId = savedSessionId ? Number.parseInt(savedSessionId, 10) : null;

    if (parsedSavedSessionId && Number.isFinite(parsedSavedSessionId) && parsedSavedSessionId > 0) {
      setCurrentSessionId(parsedSavedSessionId);
      return;
    }

    try {
      const response = await api.get('/lernbuero/sessions/today');
      const sessions = Array.isArray(response?.data?.data) ? response.data.data as LernbueroSession[] : [];
      const fallbackSessionId = sessions[0]?.SessionID ?? null;

      if (fallbackSessionId && Number.isFinite(fallbackSessionId)) {
        setCurrentSessionId(fallbackSessionId);
        sessionStorage.setItem('lernbuero-selected-session-id', fallbackSessionId.toString());
      } else {
        setCurrentSessionId(null);
      }
    } catch (error) {
      console.error('Error syncing Lernbüro session for Sitzordnung:', error);
      setCurrentSessionId(null);
    }
  }, []);

  useEffect(() => {
    syncCurrentSessionId();

    const handleFocus = () => {
      syncCurrentSessionId();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'lernbuero-selected-session-id') {
        syncCurrentSessionId();
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, [syncCurrentSessionId]);

  // Load attendance data when session ID changes
  useEffect(() => {
    if (currentSessionId) {
      loadAttendanceData();
    }
  }, [currentSessionId]);

  // Load active temporary absences for current Lernbüro session
  useEffect(() => {
    if (!currentSessionId) {
      setActiveAbsencesMap({});
      return;
    }

    let cancelled = false;
    const loadActiveAbsences = async () => {
      try {
        const response = await fetchActiveAbsences('lernbuero_session', currentSessionId);
        if (!cancelled) {
          setActiveAbsencesMap(response?.data || {});
        }
      } catch (error) {
        if (!cancelled) {
          setActiveAbsencesMap({});
        }
      }
    };

    loadActiveAbsences();
    const intervalId = window.setInterval(loadActiveAbsences, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentSessionId]);

  // Load photos for filtered students with debouncing (from Students.tsx)
  useEffect(() => {
    const updateContainerHeight = () => {
      if (listContainerRef.current) {
        setListContainerHeight(listContainerRef.current.clientHeight);
      }
    };

    updateContainerHeight();
    window.addEventListener('resize', updateContainerHeight);

    return () => {
      window.removeEventListener('resize', updateContainerHeight);
    };
  }, []);

  // Load photos for filtered students with debouncing (from Students.tsx)
  useEffect(() => {
    if (filteredStudents.length > 0) {
      const effectiveContainerHeight = listContainerHeight || 600;
      const startIndex = Math.max(0, Math.floor(listScrollTop / STUDENT_ROW_HEIGHT) - VIRTUAL_BUFFER);
      const endIndex = Math.min(
        filteredStudents.length,
        Math.ceil((listScrollTop + effectiveContainerHeight) / STUDENT_ROW_HEIGHT) + VIRTUAL_BUFFER
      );

      const visibleStudentIds = filteredStudents
        .slice(startIndex, endIndex)
        .map((s: Student) => s.sophomorixUnid)
        .filter((id: string) => {
          if (!id || id === 'undefined' || id === 'null') {
            return false;
          }

          // Skip IDs whose photo was already requested (null stays cached)
          return !Object.prototype.hasOwnProperty.call(studentPhotos, id);
        });
      
      if (visibleStudentIds.length > 0) {
        // Debounce photo loading to prevent too many requests
        const timeoutId = setTimeout(() => {
          // Load photos in smaller batches for visible students only
          const batchSize = 20; // Smaller batches for better performance
          const batches = [];
          for (let i = 0; i < visibleStudentIds.length; i += batchSize) {
            batches.push(visibleStudentIds.slice(i, i + batchSize));
          }
          
          // Load each batch with delay to prevent overwhelming the server
          batches.forEach((batch, index) => {
            setTimeout(() => {
              loadStudentPhotos(batch).then(photos => {
                // Update cache for each photo directly without using updatePhotoCache function
                setStudentPhotos(prev => {
                  const newPhotos = { ...prev };
                  Object.entries(photos).forEach(([studentId, photo]) => {
                    newPhotos[studentId] = photo;
                  });
                  
                  // Update cache order
                  setPhotoCacheOrder(prevOrder => {
                    const newOrder = [...prevOrder];
                    Object.keys(photos).forEach(studentId => {
                      // Remove from current position and add to end
                      const index = newOrder.indexOf(studentId);
                      if (index > -1) {
                        newOrder.splice(index, 1);
                      }
                      newOrder.push(studentId);
                    });
                    
                    // Remove oldest entries if cache is too large
                    if (newOrder.length > MAX_CACHE_SIZE) {
                      const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
                      toRemove.forEach(id => delete newPhotos[id]);
                    }
                    
                    return newOrder;
                  });
                  
                  return newPhotos;
                });
              }).catch(error => {
                console.warn('Fehler beim Laden der sichtbaren Schülerfotos:', error);
              });
            }, index * 200); // 200ms delay between batches
          });
        }, 300); // 300ms debounce delay
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [filteredStudents, studentPhotos, listScrollTop, listContainerHeight]); // Removed updatePhotoCache from dependencies

  // Schüler und Klassen nur laden wenn Edit-Modus aktiviert wird
  const loadStudentsAndClasses = async () => {
    try {
      // Schüler laden (OHNE Fotos für bessere Performance)
      const studentsResponse = await apiClient.get('/students', {
        params: {
          includePhotos: 'false' // Keine Fotos laden - werden nur bei Bedarf geladen
        }
      });
      if (studentsResponse.data.success) {
        const studentsData = studentsResponse.data.data || [];
        setStudents(studentsData);
        // Fotos werden nur bei Bedarf geladen (beim Öffnen eines Sitzplans)
      }
      
      // Klassen laden
      const classesResponse = await apiClient.get('/students/classes');
      if (classesResponse.data.success) {
        const classNames = classesResponse.data.data.map((c: any) => c.Klasse).filter(Boolean);
        setClasses(['alle', ...classNames]);
      }

      // Kurse/Gruppen für Sitzplan-Kontext laden
      try {
        const metaResponse = await apiClient.get('/unterrichten/meta');
        const meta = metaResponse.data?.data || {};
        setCourses((meta.courses || []).map((course: any) => ({ id: course.id, name: course.name })));
        setGroups((meta.groups || []).map((group: any) => ({ id: group.id, name: group.name })));
      } catch (metaError) {
        console.warn('Kontext-Metadaten (Kurse/Gruppen) konnten nicht geladen werden:', metaError);
      }
      
      // Lernpartnerschaften laden
      await loadPartnerships();
      
    } catch (err) {
      console.error('Fehler beim Laden der Schüler/Klassen:', err);
      toast.error('Fehler beim Laden der Schüler-Daten');
    }
  };

  // Fotos für Schüler im Sitzplan laden (nur die benötigten) - mit LRU Cache
  const loadSitzplanStudentPhotos = async (sitzplanStudents: Student[]) => {
    try {
      const sophomorixUnids = sitzplanStudents.map(s => s.sophomorixUnid);
      if (sophomorixUnids.length === 0) return;
      
      console.log(`📸 Lade Fotos für ${sophomorixUnids.length} Schüler im Sitzplan...`);
      
      // Nur Fotos für Schüler laden, die noch nicht im Cache sind
      const uncachedIds = sophomorixUnids.filter(id => !Object.prototype.hasOwnProperty.call(studentPhotos, id));
      if (uncachedIds.length === 0) {
        console.log('✅ Alle Sitzplan-Fotos bereits im Cache');
        return;
      }
      
      // Fotos in Batches von 20 laden für bessere Performance
      const batchSize = 20;
      const batches = [];
      for (let i = 0; i < uncachedIds.length; i += batchSize) {
        batches.push(uncachedIds.slice(i, i + batchSize));
      }
      
      for (const batch of batches) {
        try {
          const photos = await loadStudentPhotos(batch);
          // Update cache for each photo directly without using updatePhotoCache function
          setStudentPhotos(prev => {
            const newPhotos = { ...prev };
            Object.entries(photos).forEach(([studentId, photo]) => {
              newPhotos[studentId] = photo;
            });
            
            // Update cache order
            setPhotoCacheOrder(prevOrder => {
              const newOrder = [...prevOrder];
              Object.keys(photos).forEach(studentId => {
                // Remove from current position and add to end
                const index = newOrder.indexOf(studentId);
                if (index > -1) {
                  newOrder.splice(index, 1);
                }
                newOrder.push(studentId);
              });
              
              // Remove oldest entries if cache is too large
              if (newOrder.length > MAX_CACHE_SIZE) {
                const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
                toRemove.forEach(id => delete newPhotos[id]);
              }
              
              return newOrder;
            });
            
            return newPhotos;
          });
          
          // Kurze Pause zwischen Batches um Server nicht zu überlasten
          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.warn('Fehler beim Laden eines Foto-Batches:', error);
          // Setze null für alle IDs in diesem Batch
          setStudentPhotos(prev => {
            const newPhotos = { ...prev };
            batch.forEach(id => {
              newPhotos[id] = null;
            });
            
            // Update cache order
            setPhotoCacheOrder(prevOrder => {
              const newOrder = [...prevOrder];
              batch.forEach(studentId => {
                // Remove from current position and add to end
                const index = newOrder.indexOf(studentId);
                if (index > -1) {
                  newOrder.splice(index, 1);
                }
                newOrder.push(studentId);
              });
              
              // Remove oldest entries if cache is too large
              if (newOrder.length > MAX_CACHE_SIZE) {
                const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
                toRemove.forEach(id => delete newPhotos[id]);
              }
              
              return newOrder;
            });
            
            return newPhotos;
          });
        }
      }
      
      console.log(`✅ ${uncachedIds.length} neue Fotos für Sitzplan geladen`);
    } catch (error) {
      console.warn('Fehler beim Laden der Schülerfotos:', error);
    }
  };

  // Sitzpläne für Liste laden
  const loadSitzplaeneList = async (page = 1, search = '', contextFilter = 'all', sort = 'updated_at', order = 'DESC') => {
    try {
      const params = new URLSearchParams();
      
      // Optimierte Übersicht verwenden
      params.append('overview', 'true');
      
      if (contextFilter && contextFilter !== 'all') {
        if (contextFilter.startsWith('class:')) {
          params.append('klasse', contextFilter.replace('class:', ''));
        } else if (contextFilter.startsWith('course:')) {
          params.append('context_type', 'course');
          params.append('context_value', contextFilter.replace('course:', ''));
        } else if (contextFilter.startsWith('group:')) {
          params.append('context_type', 'group');
          params.append('context_value', contextFilter.replace('group:', ''));
        }
      }
      
      const response = await apiClient.get(`/sitzplaene?${params}`);
      if (response.data.success) {
        let sitzplaene = response.data.data || [];
        
        // Client-seitige Filterung für Suche und Sortierung
        if (search) {
          sitzplaene = sitzplaene.filter((sitzplan: Sitzplan) =>
            sitzplan.name.toLowerCase().includes(search.toLowerCase()) ||
            (sitzplan.beschreibung && sitzplan.beschreibung.toLowerCase().includes(search.toLowerCase()))
          );
        }
        
        // Sortierung
        sitzplaene.sort((a: Sitzplan, b: Sitzplan) => {
          let aValue = a[sort as keyof Sitzplan];
          let bValue = b[sort as keyof Sitzplan];
          
          // Handle undefined values
          if ((aValue === undefined || aValue === null) && (bValue === undefined || bValue === null)) return 0;
          if (aValue === undefined || aValue === null) return 1;
          if (bValue === undefined || bValue === null) return -1;
          
          if (typeof aValue === 'string') aValue = aValue.toLowerCase();
          if (typeof bValue === 'string') bValue = bValue.toLowerCase();
          
          if (order === 'ASC') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        });
        
        // Pagination (vereinfacht)
        const limit = 20;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedSitzplaene = sitzplaene.slice(startIndex, endIndex);
        const totalPages = Math.ceil(sitzplaene.length / limit);
        
        setSitzplaene(paginatedSitzplaene);
        setListTotalPages(totalPages);
        setListCurrentPage(page);
        
        // Favoriten initialisieren
        const favoriteIds = new Set<number>();
        paginatedSitzplaene.forEach((sitzplan: Sitzplan) => {
          if (sitzplan.is_favorite) {
            favoriteIds.add(sitzplan.id);
          }
        });
        setFavorites(favoriteIds);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Sitzpläne:', err);
    }
  };

  // Sitzplan laden
  const loadSitzplan = async (sitzplanId: number) => {
    try {
      const response = await apiClient.get(`/sitzplaene/${sitzplanId}`);
      if (response.data.success) {
        const sitzplanData = response.data.data;
        setCurrentSitzplan(sitzplanData);
        const nextContextType: SitzplanContextType =
          sitzplanData.context_type || (sitzplanData.klasse ? 'class' : 'class');
        const nextContextValue =
          sitzplanData.context_value || (sitzplanData.klasse ? String(sitzplanData.klasse) : 'alle');
        setSelectedContextType(nextContextType);
        setSelectedClass(nextContextValue);
        setGridRows(sitzplanData.grid_rows || 5);
        setGridCols(sitzplanData.grid_cols || 5);
        
        // Grid-Positionen erstellen
        const totalPositions = (sitzplanData.grid_rows || 5) * (sitzplanData.grid_cols || 5);
        const newGridPositions: GridPosition[] = Array(totalPositions).fill(null).map((_, index) => ({
          studentId: null,
          position: index
        }));
        
        // Positionen aus der Datenbank laden
        if (sitzplanData.positionen) {
          sitzplanData.positionen.forEach((pos: SitzplanPosition) => {
            const gridIndex = pos.grid_row * (sitzplanData.grid_cols || 5) + pos.grid_col;
            
            if (gridIndex < newGridPositions.length && pos.schueler_id) {
              newGridPositions[gridIndex] = {
                studentId: pos.schueler_id.toString(),
                position: gridIndex,
                pinned: pos.pinned || false
              };
            }
          });
        }
        
        setGridPositions(newGridPositions);
        
        // Pinned students aus den geladenen Positionen extrahieren
        const pinnedStudentIds = new Set<string>();
        newGridPositions.forEach(pos => {
          if (pos.studentId && pos.pinned) {
            pinnedStudentIds.add(pos.studentId);
          }
        });
        setPinnedStudents(pinnedStudentIds);
        
        // OPTIMIERT: Schüler aus dem Sitzplan verwenden (mit Fotos)
        // Diese Schüler haben bereits ihre Fotos vom Backend geladen
        const sitzplanStudentsData: Student[] = sitzplanData.positionen
          .filter((pos: SitzplanPosition) => pos.schueler_id)
          .map((pos: SitzplanPosition) => ({
            ID: pos.schueler_id.toString(),
            sophomorixUnid: pos.schueler_id.toString(),
            Name: pos.name,
            Vorname: pos.vorname,
            Klasse: pos.klasse,
            Memo: pos.memo,
            Foto: pos.foto // Foto ist bereits vom Backend geladen!
          }));
        
        setSitzplanStudents(sitzplanStudentsData);
        
        // Fotos für die Schüler im Sitzplan laden
        await loadSitzplanStudentPhotos(sitzplanStudentsData);
        
        // Schüler aus der Tabelle entfernen, die bereits im Grid sind
        const studentsInGrid = newGridPositions
          .filter(pos => pos.studentId)
          .map(pos => pos.studentId!);
        
        // Filtere Schüler basierend auf aktueller Klassenauswahl
        const classFilteredStudents = students.filter(student => {
          if (selectedContextType === 'class' && selectedClass !== 'alle' && student.Klasse !== selectedClass) {
            return false;
          }
          if ((selectedContextType === 'course' || selectedContextType === 'group') && selectedClass !== 'alle') {
            const allowedIds = contextStudentIds || new Set<string>();
            const studentId = String(student.sophomorixUnid || student.ID);
            if (!allowedIds.has(studentId) && !allowedIds.has(String(student.ID))) {
              return false;
            }
          }
          return !studentsInGrid.includes(student.sophomorixUnid);
        });
        
        setFilteredStudents(classFilteredStudents);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Sitzplans:', err);
      setError('Fehler beim Laden des Sitzplans');
    }
  };

  // Neuen Sitzplan erstellen
  const createNewSitzplan = async () => {
    // LAZY LOADING: Schüler und Klassen nur laden wenn neuer Sitzplan erstellt wird
    if (students.length === 0) {
      await loadStudentsAndClasses();
    }
    
    setCurrentSitzplan(null);
    setSelectedContextType('class');
    setSelectedClass('alle');
    setGridRows(8);
    setGridCols(8);
    const totalPositions = 8 * 8;
    setGridPositions(Array(totalPositions).fill(null).map((_, index) => ({
      studentId: null,
      position: index,
      pinned: false
    })));
    
    // Pinned students zurücksetzen
    setPinnedStudents(new Set());
    
    // Sitzplan-Schüler zurücksetzen (neuer Sitzplan hat keine Schüler)
    setSitzplanStudents([]);
    
    // Schüler basierend auf aktueller Filterung in die Tabelle bringen
    // (Die useEffect wird automatisch die korrekte Filterung anwenden)
  };


  // Sitzplan speichern
  const handleSaveSitzplan = async () => {
    const defaultName = currentSitzplan ? currentSitzplan.name : '';
    const name = prompt('Name des Sitzplans:', defaultName);
    if (!name) return;
    
    const defaultBeschreibung = currentSitzplan ? currentSitzplan.beschreibung || '' : '';
    const beschreibung = prompt('Beschreibung (optional):', defaultBeschreibung);
    
    // Prüfen ob Sitzplan bereits existiert
    if (currentSitzplan) {
      const saveAction = await showSaveDialog(currentSitzplan.name);
      
      if (saveAction === 'overwrite') {
        await saveSitzplan(name, beschreibung || undefined, 'overwrite');
      } else if (saveAction === 'clone') {
        await saveSitzplan(name, beschreibung || undefined, 'clone');
      }
      // Wenn saveAction null ist, wurde abgebrochen
    } else {
      // Neuer Sitzplan
    await saveSitzplan(name, beschreibung || undefined);
    }
  };

  const showSaveDialog = (sitzplanName: string): Promise<'overwrite' | 'clone' | null> => {
    return new Promise((resolve) => {
      // Erstelle einen schönen Dialog anstatt confirm()
      const dialog = document.createElement('div');
      dialog.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      
      const content = document.createElement('div');
      content.style.cssText = `
        background: white;
        border-radius: 12px;
        padding: 24px;
        max-width: 400px;
        width: 90%;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      `;
      
      content.innerHTML = `
        <div style="margin-bottom: 16px;">
          <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1f2937;">
            Sitzplan speichern
          </h3>
          <p style="margin: 0; color: #6b7280; font-size: 14px; line-height: 1.5;">
            Der Sitzplan "<strong>${sitzplanName}</strong>" existiert bereits.
          </p>
        </div>
        
        <div style="margin-bottom: 20px;">
          <p style="margin: 0 0 12px 0; color: #374151; font-size: 14px;">
            Wie möchten Sie fortfahren?
          </p>
        </div>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="cancel-btn" style="
            padding: 8px 16px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: white;
            color: #374151;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          ">Abbrechen</button>
          <button id="clone-btn" style="
            padding: 8px 16px;
            border: 1px solid #3b82f6;
            border-radius: 6px;
            background: white;
            color: #3b82f6;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          ">Als Kopie speichern</button>
          <button id="overwrite-btn" style="
            padding: 8px 16px;
            border: 1px solid #3b82f6;
            border-radius: 6px;
            background: #3b82f6;
            color: white;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          ">Überschreiben</button>
        </div>
      `;
      
      dialog.appendChild(content);
      document.body.appendChild(dialog);
      
      // Event Listeners
      const cancelBtn = content.querySelector('#cancel-btn');
      const cloneBtn = content.querySelector('#clone-btn');
      const overwriteBtn = content.querySelector('#overwrite-btn');
      
      let cleanup = () => {
        document.body.removeChild(dialog);
      };
      
      cancelBtn?.addEventListener('click', () => {
        cleanup();
        resolve(null);
      });
      
      cloneBtn?.addEventListener('click', () => {
        cleanup();
        resolve('clone');
      });
      
      overwriteBtn?.addEventListener('click', () => {
        cleanup();
        resolve('overwrite');
      });
      
      // ESC-Taste zum Schließen
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          cleanup();
          resolve(null);
        }
      };
      
      document.addEventListener('keydown', handleKeydown);
      
      // Cleanup beim Schließen
      const originalCleanup = cleanup;
      cleanup = () => {
        document.removeEventListener('keydown', handleKeydown);
        originalCleanup();
      };
    });
  };

  const showCloneDialog = (sitzplan: Sitzplan): Promise<{name: string, beschreibung: string} | null> => {
    return new Promise((resolve) => {
      const dialog = document.createElement('div');
      dialog.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      
      const content = document.createElement('div');
      content.style.cssText = `
        background: white;
        border-radius: 12px;
        padding: 24px;
        max-width: 450px;
        width: 90%;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      `;
      
      content.innerHTML = `
        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1f2937;">
            Sitzplan als Kopie erstellen
          </h3>
          <p style="margin: 0; color: #6b7280; font-size: 14px; line-height: 1.5;">
            Sie erstellen eine Kopie von "<strong>${sitzplan.name}</strong>".
          </p>
        </div>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151;">
            Name der Kopie
          </label>
          <input id="clone-name" type="text" value="${sitzplan.name} (Kopie)" style="
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            box-sizing: border-box;
          " />
        </div>
        
        <div style="margin-bottom: 20px;">
          <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151;">
            Beschreibung (optional)
          </label>
          <textarea id="clone-description" style="
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            box-sizing: border-box;
            resize: vertical;
            min-height: 60px;
          ">${sitzplan.beschreibung || ''}</textarea>
        </div>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="cancel-clone-btn" style="
            padding: 8px 16px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: white;
            color: #374151;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          ">Abbrechen</button>
          <button id="create-clone-btn" style="
            padding: 8px 16px;
            border: 1px solid #10b981;
            border-radius: 6px;
            background: #10b981;
            color: white;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          ">Kopie erstellen</button>
        </div>
      `;
      
      dialog.appendChild(content);
      document.body.appendChild(dialog);
      
      // Focus auf das Name-Feld
      const nameInput = content.querySelector('#clone-name') as HTMLInputElement;
      nameInput?.focus();
      nameInput?.select();
      
      // Event Listeners
      const cancelBtn = content.querySelector('#cancel-clone-btn');
      const createBtn = content.querySelector('#create-clone-btn');
      const nameField = content.querySelector('#clone-name') as HTMLInputElement;
      const descField = content.querySelector('#clone-description') as HTMLTextAreaElement;
      
      let cleanup = () => {
        document.body.removeChild(dialog);
      };
      
      cancelBtn?.addEventListener('click', () => {
        cleanup();
        resolve(null);
      });
      
      createBtn?.addEventListener('click', () => {
        const name = nameField?.value?.trim();
        if (!name) {
          toast.error('Bitte geben Sie einen Namen für die Kopie ein.');
          return;
        }
        cleanup();
        resolve({
          name,
          beschreibung: descField?.value?.trim() || ''
        });
      });
      
      // Enter-Taste zum Erstellen
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          (createBtn as HTMLButtonElement)?.click();
        } else if (e.key === 'Escape') {
          cleanup();
          resolve(null);
        }
      };
      
      document.addEventListener('keydown', handleKeydown);
      
      // Cleanup beim Schließen
      const originalCleanup = cleanup;
      cleanup = () => {
        document.removeEventListener('keydown', handleKeydown);
        originalCleanup();
      };
    });
  };

  const saveSitzplan = async (name: string, beschreibung?: string, action?: 'overwrite' | 'clone') => {
    try {
      setSaving(true);

      const contextValue = selectedClass !== 'alle' ? selectedClass : null;
      const contextLabel =
        selectedContextType === 'class'
          ? (contextValue || null)
          : selectedContextType === 'course'
          ? (courses.find((course) => String(course.id) === String(contextValue))?.name || null)
          : (groups.find((group) => String(group.id) === String(contextValue))?.name || null);
      const effectiveClass = selectedContextType === 'class' ? contextValue : null;
      
      const sitzplanData = {
        name,
        beschreibung,
        ersteller_name: user?.username || 'Unbekannt',
        klasse: effectiveClass,
        context_type: contextValue ? selectedContextType : null,
        context_value: contextValue,
        context_label: contextLabel,
        grid_rows: gridRows,
        grid_cols: gridCols,
        is_public: isReadOnly, // Read-only Freigabe
        is_readonly: isReadOnly,
        positionen: gridPositions
          .map((pos, index) => ({
            grid_row: Math.floor(index / gridCols),
            grid_col: index % gridCols,
            schueler_id: pos.studentId ? parseInt(pos.studentId) : null,
            pinned: pos.pinned || false
          }))
          .filter(pos => pos.schueler_id !== null)
      };
      
      let response;
      if (currentSitzplan && action === 'overwrite') {
        // Bestehenden Sitzplan überschreiben (einfach die normale PUT-Route verwenden)
        response = await apiClient.put(`/sitzplaene/${currentSitzplan.id}`, sitzplanData);
      } else if (currentSitzplan && action === 'clone') {
        // Neuen Sitzplan als Kopie erstellen
        response = await apiClient.post(`/sitzplaene/${currentSitzplan.id}/clone`, {
          name: name || `${currentSitzplan.name} (Kopie)`,
          beschreibung: beschreibung || currentSitzplan.beschreibung,
          ersteller_name: user?.username,
          klasse: effectiveClass || currentSitzplan.klasse,
          context_type: contextValue ? selectedContextType : currentSitzplan.context_type,
          context_value: contextValue || currentSitzplan.context_value,
          context_label: contextLabel || currentSitzplan.context_label
        });
        
        // Positionen für die Kopie aktualisieren
        if (response.data.success && response.data.data.id) {
          await apiClient.put(`/sitzplaene/${response.data.data.id}/positionen`, {
            positionen: sitzplanData.positionen
          });
        }
      } else if (currentSitzplan) {
        // Normale Aktualisierung
        response = await apiClient.put(`/sitzplaene/${currentSitzplan.id}`, sitzplanData);
      } else {
        // Neuen Sitzplan erstellen
        response = await apiClient.post('/sitzplaene', sitzplanData);
      }
      
      if (response.data.success) {
        const newSitzplanId = response.data.data?.id || currentSitzplan?.id;
        if (newSitzplanId) {
          setCurrentSitzplan({
            ...currentSitzplan,
            id: newSitzplanId,
            name,
            beschreibung,
            context_type: contextValue ? selectedContextType : currentSitzplan?.context_type || null,
            context_value: contextValue || currentSitzplan?.context_value || null,
            context_label: contextLabel || currentSitzplan?.context_label || null
          });
        }
        await loadSitzplaeneList(); // Liste aktualisieren
        toast.success('Sitzplan erfolgreich gespeichert!');
        
        // Zur vorherigen Ansicht zurückkehren
        if (previousView === 'view' && currentSitzplan) {
          // Wenn von der View-Ansicht gekommen, zurück zur View-Ansicht
          setCurrentView('view');
        } else if (previousView === 'list') {
          // Wenn von der Liste gekommen, zurück zur Liste
          setCurrentView('list');
          setCurrentSitzplan(null);
        } else {
          // Fallback: Zur Liste zurückkehren
          setCurrentView('list');
          setCurrentSitzplan(null);
        }
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      toast.error('Fehler beim Speichern des Sitzplans');
    } finally {
      setSaving(false);
    }
  };

  // PDF Export - Schönes DIN A4 Layout mit Namen und Fotos
  const exportSitzplanAsPDF = async () => {
    if (!currentSitzplan) {
      toast.error('Kein Sitzplan zum Exportieren ausgewählt');
      return;
    }

    // Sammle alle Schüler aus dem Grid mit ihren Positionen
    const studentsInGrid: Array<{
      row: number;
      col: number;
      student: Student;
      photoUrl: string | null;
    }> = [];

    gridPositions.forEach((position, index) => {
      if (position.studentId) {
        const student = getStudentById(position.studentId);
        if (student) {
          const row = Math.floor(index / gridCols);
          const col = index % gridCols;
          studentsInGrid.push({
            row,
            col,
            student,
            photoUrl: studentPhotos[student.sophomorixUnid] || null
          });
        }
      }
    });

    // Finde die tatsächlich belegten Zeilen und Spalten
    const usedRows = new Set<number>();
    const usedCols = new Set<number>();
    studentsInGrid.forEach(({ row, col }) => {
      usedRows.add(row);
      usedCols.add(col);
    });
    
    const sortedRows = Array.from(usedRows).sort((a, b) => a - b);
    const sortedCols = Array.from(usedCols).sort((a, b) => a - b);

    // Erstelle HTML für das PDF
    const filename = currentSitzplan.name.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_');
    const today = new Date().toLocaleDateString('de-DE');
    
    // Erstelle Grid-HTML nur mit belegten Positionen
    let gridHTML = '';
    sortedRows.forEach(row => {
      sortedCols.forEach(col => {
        const entry = studentsInGrid.find(s => s.row === row && s.col === col);
        if (entry) {
          const photoHTML = entry.photoUrl 
            ? `<img src="${createPhotoDataUrl(entry.photoUrl)}" alt="${entry.student.Vorname} ${entry.student.Name}" />`
            : `<div class="no-photo">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>`;
          
          gridHTML += `
            <div class="student-cell">
              <div class="photo-container">
                ${photoHTML}
              </div>
              <div class="student-name">${entry.student.Vorname} ${entry.student.Name}</div>
            </div>
          `;
        } else {
          gridHTML += `<div class="student-cell empty"></div>`;
        }
      });
    });

    // Öffne Druckfenster mit dem Layout
    const printWindow = window.open('', '_blank', 'width=900,height=700,menubar=yes,toolbar=yes,scrollbars=yes,resizable=yes');
    
    if (!printWindow) {
      toast.error('Popup-Blocker verhindert das Öffnen des Druckfensters. Bitte erlauben Sie Popups für diese Seite.');
      return;
    }

    const htmlContent = `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sitzplan: ${currentSitzplan.name}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            background: #f8f9fa;
            padding: 20px;
            color: #333;
        }
        
        .print-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        
        .header {
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            color: white;
            padding: 24px 32px;
            text-align: center;
        }
        
        .header h1 {
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
            letter-spacing: -0.5px;
        }
        
        .header .meta {
            font-size: 14px;
            opacity: 0.9;
        }
        
        .grid-wrapper {
            padding: 32px;
        }
        
        .tafel {
            background: linear-gradient(180deg, #059669 0%, #047857 100%);
            color: white;
            text-align: center;
            padding: 12px 24px;
            border-radius: 8px;
            margin-top: 24px;
            font-weight: 600;
            letter-spacing: 1px;
            text-transform: uppercase;
            font-size: 13px;
            box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
        }
        
        .student-grid {
            display: grid;
            grid-template-columns: repeat(${sortedCols.length}, 1fr);
            gap: 16px;
            justify-items: center;
        }
        
        .student-cell {
            width: 100%;
            max-width: 120px;
            text-align: center;
        }
        
        .student-cell.empty {
            visibility: hidden;
        }
        
        .photo-container {
            width: 100%;
            aspect-ratio: 1;
            border-radius: 12px;
            overflow: hidden;
            background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
            margin-bottom: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .photo-container img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        .photo-container .no-photo {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
        }
        
        .photo-container .no-photo svg {
            width: 40%;
            height: 40%;
            color: white;
            opacity: 0.7;
        }
        
        .student-name {
            font-size: 12px;
            font-weight: 600;
            color: #374151;
            line-height: 1.3;
            word-break: break-word;
        }
        
        .footer {
            text-align: center;
            padding: 16px 32px 24px;
            color: #9ca3af;
            font-size: 11px;
            border-top: 1px solid #e5e7eb;
        }
        
        .print-button {
            position: fixed;
            bottom: 30px;
            right: 30px;
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            color: white;
            border: none;
            padding: 16px 28px;
            border-radius: 50px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 10px 30px rgba(79, 70, 229, 0.4);
            transition: all 0.3s ease;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        
        .print-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 15px 40px rgba(79, 70, 229, 0.5);
        }
        
        /* Print-spezifische Styles */
        @media print {
            @page {
                margin: 15mm;
                size: A4 portrait;
            }
            
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            
            body {
                background: white !important;
                padding: 0 !important;
            }
            
            .print-container {
                max-width: none !important;
                box-shadow: none !important;
                border-radius: 0 !important;
            }
            
            .header {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            
            .tafel {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            
            .photo-container {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            
            .student-grid {
                gap: 12px;
            }
            
            .student-cell {
                max-width: 100px;
            }
            
            .student-name {
                font-size: 10px;
            }
            
            .print-button {
                display: none !important;
            }
            
            .footer {
                display: none !important;
            }
        }
    </style>
</head>
<body>
    <div class="print-container">
        <div class="header">
            <h1>${currentSitzplan.name}</h1>
            <div class="meta">
                ${currentSitzplan.klasse ? `Klasse: ${currentSitzplan.klasse} • ` : ''}
                ${studentsInGrid.length} Schüler • ${today}
            </div>
        </div>
        
        <div class="grid-wrapper">
            <div class="student-grid">
                ${gridHTML}
            </div>
            <div class="tafel">Tafel</div>
        </div>
        
        <div class="footer">
            Erstellt mit Lernrakete • ${today}
        </div>
    </div>
    
    <button class="print-button" onclick="window.print()">
        <span>🖨️</span>
        <span>PDF drucken</span>
    </button>
    
    <script>
        // Warte bis alle Bilder geladen sind, bevor der Druckdialog geöffnet wird
        function waitForImages() {
            const images = document.querySelectorAll('img');
            if (images.length === 0) {
                setTimeout(() => window.print(), 300);
                return;
            }
            
            let loadedCount = 0;
            const totalImages = images.length;
            
            function checkAllLoaded() {
                loadedCount++;
                if (loadedCount >= totalImages) {
                    // Alle Bilder geladen - kurze Verzögerung für Rendering
                    setTimeout(() => window.print(), 300);
                }
            }
            
            images.forEach(img => {
                if (img.complete && img.naturalHeight !== 0) {
                    checkAllLoaded();
                } else {
                    img.onload = checkAllLoaded;
                    img.onerror = checkAllLoaded; // Auch bei Fehler weitermachen
                }
            });
            
            // Fallback: Nach 5 Sekunden trotzdem drucken
            setTimeout(() => {
                if (loadedCount < totalImages) {
                    console.log('Timeout: Drucke trotz ungeladener Bilder');
                    window.print();
                }
            }, 5000);
        }
        
        // Starte wenn DOM bereit ist
        if (document.readyState === 'complete') {
            waitForImages();
        } else {
            window.addEventListener('load', waitForImages);
        }
    </script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    toast.success('Druckvorschau geöffnet!');
  };

  // Export (XLSX - legacy)
  const exportSitzplanXLSX = async (sitzplanId?: number, sitzplanName?: string) => {
    const targetSitzplanId = sitzplanId || currentSitzplan?.id;
    const targetSitzplanName = sitzplanName || currentSitzplan?.name;
    
    if (!targetSitzplanId || !targetSitzplanName) {
      toast.error('Kein Sitzplan zum Exportieren ausgewählt');
      return;
    }
    
    try {
      console.log('Exportiere Sitzplan:', { targetSitzplanId, targetSitzplanName });
      
      // XLSX-Export anfordern
      const response = await apiClient.get(`/sitzplaene/${targetSitzplanId}/export?format=xlsx`, {
        responseType: 'blob'
      });
      
      // Datei herunterladen
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sitzplan_${targetSitzplanName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Sitzplan erfolgreich exportiert!');
    } catch (err: any) {
      console.error('Fehler beim Exportieren:', err);
      
      if (err.response?.status === 401) {
        toast.error('Sie müssen angemeldet sein, um Sitzpläne zu exportieren');
      } else if (err.response?.status === 403) {
        toast.error('Keine Berechtigung zum Exportieren dieses Sitzplans');
      } else if (err.response?.status === 404) {
        toast.error('Sitzplan nicht gefunden');
      } else {
        toast.error(`Fehler beim Exportieren: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  // PDF Export direkt aus der Liste (lädt Daten temporär)
  const exportSitzplanAsPDFFromList = async (sitzplanId: number, sitzplanName: string) => {
    // WICHTIG: Fenster SOFORT öffnen (synchron), bevor async Operationen!
    // Browser blockieren window.open() nach async Calls als Popup
    const printWindow = window.open('', '_blank', 'width=900,height=700,menubar=yes,toolbar=yes,scrollbars=yes,resizable=yes');
    
    if (!printWindow) {
      toast.error('Popup-Blocker verhindert das Öffnen des Druckfensters. Bitte erlauben Sie Popups für diese Seite.');
      return;
    }

    // Zeige Ladeanimation im Fenster
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sitzplan wird geladen...</title>
        <style>
          body {
            font-family: 'Segoe UI', system-ui, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            margin: 0;
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          }
          .loader {
            text-align: center;
            color: white;
          }
          .spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      </head>
      <body>
        <div class="loader">
          <div class="spinner"></div>
          <h2>Sitzplan wird geladen...</h2>
          <p>Fotos werden vorbereitet</p>
        </div>
      </body>
      </html>
    `);

    try {
      // 1. Sitzplan-Daten laden
      const response = await apiClient.get(`/sitzplaene/${sitzplanId}`);
      if (!response.data.success) {
        printWindow.document.body.innerHTML = '<div style="padding:40px;text-align:center;color:#dc2626;"><h2>Fehler beim Laden des Sitzplans</h2><button onclick="window.close()" style="margin-top:20px;padding:10px 20px;cursor:pointer;">Schließen</button></div>';
        return;
      }

      const sitzplanData = response.data.data;
      const positions: SitzplanPosition[] = sitzplanData.positionen || [];
      const klasse = sitzplanData.klasse || '';

      // 2. Fotos laden
      const sophomorixUnids = positions.map((p: SitzplanPosition) => {
        return `${p.vorname.toLowerCase()}.${p.name.toLowerCase()}`;
      });

      let photoMap: Record<string, string | null> = {};
      if (sophomorixUnids.length > 0) {
        try {
          const photoResults = await loadStudentPhotos(sophomorixUnids);
          photoMap = photoResults;
        } catch (photoErr) {
          console.warn('Fotos konnten nicht geladen werden:', photoErr);
        }
      }

      // 3. Grid-Positionen aufbauen
      type GridStudentEntry = {
        row: number;
        col: number;
        vorname: string;
        name: string;
        photoUrl: string | null;
      };
      
      const studentsInGrid: GridStudentEntry[] = [];
      positions.forEach((pos: SitzplanPosition) => {
        const sophomorixUnid = `${pos.vorname.toLowerCase()}.${pos.name.toLowerCase()}`;
        studentsInGrid.push({
          row: pos.grid_row,
          col: pos.grid_col,
          vorname: pos.vorname,
          name: pos.name,
          photoUrl: photoMap[sophomorixUnid] || null
        });
      });

      // Finde die tatsächlich belegten Zeilen und Spalten
      const usedRows = new Set<number>();
      const usedCols = new Set<number>();
      studentsInGrid.forEach(({ row, col }) => {
        usedRows.add(row);
        usedCols.add(col);
      });
      
      const sortedRows = Array.from(usedRows).sort((a, b) => a - b);
      const sortedCols = Array.from(usedCols).sort((a, b) => a - b);

      if (sortedRows.length === 0 || sortedCols.length === 0) {
        printWindow.document.body.innerHTML = '<div style="padding:40px;text-align:center;color:#dc2626;"><h2>Keine Schüler im Sitzplan gefunden</h2><button onclick="window.close()" style="margin-top:20px;padding:10px 20px;cursor:pointer;">Schließen</button></div>';
        return;
      }

      // 4. HTML für das PDF erstellen
      const today = new Date().toLocaleDateString('de-DE');
      
      let gridHTML = '';
      sortedRows.forEach(row => {
        sortedCols.forEach(col => {
          const entry = studentsInGrid.find(s => s.row === row && s.col === col);
          if (entry) {
            const photoHTML = entry.photoUrl 
              ? `<img src="${createPhotoDataUrl(entry.photoUrl)}" alt="${entry.vorname} ${entry.name}" />`
              : `<div class="no-photo">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>`;
            
            gridHTML += `
              <div class="student-cell">
                <div class="photo-container">
                  ${photoHTML}
                </div>
                <div class="student-name">${entry.vorname} ${entry.name}</div>
              </div>
            `;
          } else {
            gridHTML += `<div class="student-cell empty"></div>`;
          }
        });
      });

      // 5. Inhalt ins bereits geöffnete Fenster schreiben
      const htmlContent = `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sitzplan: ${sitzplanName}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            background: #f8f9fa;
            padding: 20px;
            color: #333;
        }
        .print-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            color: white;
            padding: 24px 32px;
            text-align: center;
        }
        .header h1 {
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
        }
        .header .meta {
            font-size: 14px;
            opacity: 0.9;
        }
        .grid-wrapper { padding: 32px; }
        .tafel {
            background: linear-gradient(180deg, #059669 0%, #047857 100%);
            color: white;
            text-align: center;
            padding: 12px 24px;
            border-radius: 8px;
            margin-top: 24px;
            font-weight: 600;
            letter-spacing: 1px;
            text-transform: uppercase;
            font-size: 13px;
            box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
        }
        .student-grid {
            display: grid;
            grid-template-columns: repeat(${sortedCols.length}, 1fr);
            gap: 16px;
            justify-items: center;
        }
        .student-cell {
            width: 100%;
            max-width: 120px;
            text-align: center;
        }
        .student-cell.empty { visibility: hidden; }
        .photo-container {
            width: 100%;
            aspect-ratio: 1;
            border-radius: 12px;
            overflow: hidden;
            background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
            margin-bottom: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .photo-container img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        .photo-container .no-photo {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
        }
        .photo-container .no-photo svg {
            width: 40%;
            height: 40%;
            color: white;
            opacity: 0.7;
        }
        .student-name {
            font-size: 12px;
            font-weight: 600;
            color: #374151;
            line-height: 1.3;
        }
        .footer {
            text-align: center;
            padding: 16px 32px 24px;
            color: #9ca3af;
            font-size: 11px;
            border-top: 1px solid #e5e7eb;
        }
        .print-button {
            position: fixed;
            bottom: 30px;
            right: 30px;
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            color: white;
            border: none;
            padding: 16px 28px;
            border-radius: 50px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 10px 30px rgba(79, 70, 229, 0.4);
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .print-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 15px 40px rgba(79, 70, 229, 0.5);
        }
        @media print {
            @page { margin: 15mm; size: A4 portrait; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            body { background: white !important; padding: 0 !important; }
            .print-container { max-width: none !important; box-shadow: none !important; border-radius: 0 !important; }
            .student-grid { gap: 12px; }
            .student-cell { max-width: 100px; }
            .student-name { font-size: 10px; }
            .print-button, .footer { display: none !important; }
        }
    </style>
</head>
<body>
    <div class="print-container">
        <div class="header">
            <h1>${sitzplanName}</h1>
            <div class="meta">
                ${klasse ? `Klasse: ${klasse} • ` : ''}${studentsInGrid.length} Schüler • ${today}
            </div>
        </div>
        <div class="grid-wrapper">
            <div class="student-grid">${gridHTML}</div>
            <div class="tafel">Tafel</div>
        </div>
        <div class="footer">Erstellt mit Lernrakete • ${today}</div>
    </div>
    <button class="print-button" onclick="window.print()">
        <span>🖨️</span>
        <span>PDF drucken</span>
    </button>
    <script>
        // Warte bis alle Bilder geladen sind, bevor der Druckdialog geöffnet wird
        function waitForImages() {
            const images = document.querySelectorAll('img');
            if (images.length === 0) {
                setTimeout(() => window.print(), 300);
                return;
            }
            
            let loadedCount = 0;
            const totalImages = images.length;
            
            function checkAllLoaded() {
                loadedCount++;
                if (loadedCount >= totalImages) {
                    // Alle Bilder geladen - kurze Verzögerung für Rendering
                    setTimeout(() => window.print(), 300);
                }
            }
            
            images.forEach(img => {
                if (img.complete && img.naturalHeight !== 0) {
                    checkAllLoaded();
                } else {
                    img.onload = checkAllLoaded;
                    img.onerror = checkAllLoaded; // Auch bei Fehler weitermachen
                }
            });
            
            // Fallback: Nach 5 Sekunden trotzdem drucken
            setTimeout(() => {
                if (loadedCount < totalImages) {
                    console.log('Timeout: Drucke trotz ungeladener Bilder');
                    window.print();
                }
            }, 5000);
        }
        
        // Starte wenn DOM bereit ist
        if (document.readyState === 'complete') {
            waitForImages();
        } else {
            window.addEventListener('load', waitForImages);
        }
    </script>
</body>
</html>`;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
      toast.success('Druckvorschau geöffnet!');
    } catch (err: any) {
      console.error('Fehler beim PDF-Export:', err);
      if (printWindow && !printWindow.closed) {
        printWindow.document.body.innerHTML = '<div style="padding:40px;text-align:center;color:#dc2626;"><h2>Fehler beim Erstellen des PDF-Exports</h2><button onclick="window.close()" style="margin-top:20px;padding:10px 20px;cursor:pointer;">Schließen</button></div>';
      }
      toast.error('Fehler beim Erstellen des PDF-Exports');
    }
  };

  // Favorites
  const toggleFavorite = async (sitzplanId: number) => {
    try {
      if (favorites.has(sitzplanId)) {
        // Remove from favorites
        await apiClient.delete(`/sitzplaene/${sitzplanId}/favorite`);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(sitzplanId);
          return newSet;
        });
        toast.success('Favorit entfernt');
      } else {
        // Add to favorites
        await apiClient.post(`/sitzplaene/${sitzplanId}/favorite`);
        setFavorites(prev => new Set(prev).add(sitzplanId));
        toast.success('Als Favorit markiert');
      }
      
      // Liste neu laden um die Sortierung zu aktualisieren
      await loadSitzplaeneList();
    } catch (err) {
      console.error('Fehler beim Toggle Favorit:', err);
      toast.error('Fehler beim Ändern des Favoriten-Status');
    }
  };

  // Sitzplan löschen
  const deleteSitzplan = async (sitzplanId: number, sitzplanName: string) => {
    if (!confirm(`Möchten Sie den Sitzplan "${sitzplanName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    
    try {
      const response = await apiClient.delete(`/sitzplaene/${sitzplanId}`);
      if (response.data.success) {
        toast.success('Sitzplan erfolgreich gelöscht!');
        await loadSitzplaeneList(); // Liste aktualisieren
      }
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      toast.error('Fehler beim Löschen des Sitzplans');
    }
  };

  // View Navigation
  const handleViewSitzplan = async (sitzplan: Sitzplan) => {
    setPreviousView(currentView); // Aktuelle Ansicht als vorherige speichern
    
    // LAZY LOADING: Schüler und Klassen nur laden wenn View-Modus aktiviert wird
    if (students.length === 0) {
      await loadStudentsAndClasses();
    }
    
    await loadSitzplan(sitzplan.id);
    setCurrentView('view');
  };

  const handleEditSitzplan = async (sitzplan?: Sitzplan) => {
    setPreviousView(currentView); // Aktuelle Ansicht als vorherige speichern
    
    // LAZY LOADING: Schüler und Klassen nur laden wenn Edit-Modus aktiviert wird
    if (students.length === 0) {
      await loadStudentsAndClasses();
    }
    
    if (sitzplan) {
      if (canEdit(sitzplan)) {
        // Eigener Sitzplan: Normal bearbeiten
        await loadSitzplan(sitzplan.id);
        setCurrentView('edit');
      } else if (canView(sitzplan)) {
        // Fremder Sitzplan: Als Kopie erstellen
        const cloneName = prompt(`Name für die Kopie von "${sitzplan.name}":`, `${sitzplan.name} (Kopie)`);
        if (!cloneName) return;
        
        try {
          console.log('Erstelle Kopie von Sitzplan:', sitzplan.id, 'mit Namen:', cloneName);
          
          const response = await apiClient.post(`/sitzplaene/${sitzplan.id}/clone`, {
            name: cloneName,
            beschreibung: sitzplan.beschreibung || undefined,
            ersteller_name: user?.username,
            klasse: sitzplan.klasse,
            context_type: sitzplan.context_type,
            context_value: sitzplan.context_value,
            context_label: sitzplan.context_label
          });
          
          console.log('Clone Response:', response.data);
          
          if (response.data.success) {
            toast.success('Sitzplan als Kopie erstellt!');
            await loadSitzplan(response.data.data.id);
            await loadSitzplaeneList(); // Liste aktualisieren um die neue Kopie anzuzeigen
            setCurrentView('edit');
          } else {
            toast.error('Fehler beim Erstellen der Kopie: ' + (response.data.message || 'Unbekannter Fehler'));
          }
        } catch (err: any) {
          console.error('Fehler beim Erstellen der Kopie:', err);
          toast.error('Fehler beim Erstellen der Kopie: ' + (err.response?.data?.message || err.message));
          return;
        }
      } else {
        toast.error('Keine Berechtigung zum Bearbeiten dieses Sitzplans');
        return;
      }
    } else {
      // Neuen Sitzplan erstellen
      await createNewSitzplan();
    }
    setCurrentView('edit');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setCurrentSitzplan(null);
    setPreviousView('list');
    // Sitzplan-Schüler zurücksetzen (zurück zur Übersicht)
    setSitzplanStudents([]);
  };

  // Liste-Filter-Funktionen
  const handleListSearch = () => {
    setListCurrentPage(1);
    loadSitzplaeneList(1, listSearchQuery, listSelectedKlasse, listSortBy, listSortOrder);
  };

  const handleListSort = (newSortBy: string) => {
    const newOrder = listSortBy === newSortBy && listSortOrder === 'ASC' ? 'DESC' : 'ASC';
    setListSortBy(newSortBy);
    setListSortOrder(newOrder);
    loadSitzplaeneList(listCurrentPage, listSearchQuery, listSelectedKlasse, newSortBy, newOrder);
  };

  const handleListPageChange = (newPage: number) => {
    setListCurrentPage(newPage);
    loadSitzplaeneList(newPage, listSearchQuery, listSelectedKlasse, listSortBy, listSortOrder);
  };

  useEffect(() => {
    let cancelled = false;

    const loadContextStudentIds = async () => {
      if (selectedContextType === 'class' || selectedClass === 'alle') {
        setContextStudentIds(null);
        return;
      }

      try {
        // Prefer memberships as single source of truth for course/group members.
        // Fallback keeps older installations (course_students/group_students) working.
        const membershipEndpoint = `/memberships/units/${selectedContextType}/${selectedClass}/members`;
        const legacyEndpoint =
          selectedContextType === 'course'
            ? `/courses/${selectedClass}/participants`
            : `/groups/${selectedClass}/participants`;

        let response;
        try {
          response = await apiClient.get(membershipEndpoint);
        } catch (membershipError) {
          response = await apiClient.get(legacyEndpoint);
        }

        const entries = response.data?.data || [];
        const ids = new Set<string>();
        entries.forEach((entry: any) => {
          const candidates = [
            entry.id,
            entry.student_id,
            entry.sophomorixUnid,
            entry.sophomorix_unid,
            entry.username
          ];
          candidates
            .filter(Boolean)
            .forEach((candidate) => ids.add(String(candidate)));
        });
        if (!cancelled) {
          setContextStudentIds(ids);
        }
      } catch (loadError) {
        console.error('Fehler beim Laden der Kontext-Teilnehmer:', loadError);
        if (!cancelled) {
          setContextStudentIds(new Set());
        }
      }
    };

    loadContextStudentIds();
    return () => {
      cancelled = true;
    };
  }, [selectedContextType, selectedClass]);

  // Filter anwenden
  useEffect(() => {
    let filtered = students;
    
    // Erst nach Kontext filtern
    if (selectedContextType === 'class' && selectedClass !== 'alle') {
      filtered = filtered.filter(student => student.Klasse === selectedClass);
    }
    if ((selectedContextType === 'course' || selectedContextType === 'group') && selectedClass !== 'alle') {
      const allowedIds = contextStudentIds || new Set<string>();
      filtered = filtered.filter((student) => {
        const studentId = String(student.sophomorixUnid || student.ID);
        return allowedIds.has(studentId) || allowedIds.has(String(student.ID));
      });
    }
    
    // Dann nach Suchbegriff filtern
    if (searchTerm) {
      filtered = filtered.filter(student => 
        student.Vorname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.Memo && student.Memo.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Schüler entfernen, die bereits im Grid sind
    const studentsInGrid = gridPositions
      .filter(pos => pos.studentId)
      .map(pos => pos.studentId!);
    
    filtered = filtered.filter(student => !studentsInGrid.includes(student.sophomorixUnid));
    
    setFilteredStudents(filtered);
  }, [students, searchTerm, selectedClass, selectedContextType, contextStudentIds, gridPositions]);

  // Grid-Größe ändern
  const handleGridSizeChange = (newRows: number, newCols: number) => {
    const oldRows = gridRows;
    const oldCols = gridCols;
    const newTotal = newRows * newCols;
    
    const newPositions: GridPosition[] = Array(newTotal).fill(null).map((_, index) => ({
      studentId: null,
      position: index
    }));
    
    // Bestehende Positionen übertragen (nur wenn sie im neuen Grid Platz haben)
    gridPositions.forEach((pos, oldIndex) => {
      if (pos.studentId) {
        const oldRow = Math.floor(oldIndex / oldCols);
        const oldCol = oldIndex % oldCols;
        
        // Prüfen ob Position im neuen Grid existiert
        if (oldRow < newRows && oldCol < newCols) {
          const newIndex = oldRow * newCols + oldCol;
          newPositions[newIndex] = pos;
        }
      }
    });
    
    setGridRows(newRows);
    setGridCols(newCols);
    setGridPositions(newPositions);
  };

  // Grid erweitern (nur vergrößern, nie verkleinern)
  const expandGrid = (direction: 'right' | 'bottom' | 'both') => {
    let newRows = gridRows;
    let newCols = gridCols;
    
    if (direction === 'right' || direction === 'both') {
      newCols = Math.min(newCols + 1, 10); // Max 10 Spalten
    }
    
    if (direction === 'bottom' || direction === 'both') {
      newRows = Math.min(newRows + 1, 10); // Max 10 Reihen
    }
    
    if (newRows !== gridRows || newCols !== gridCols) {
      handleGridSizeChange(newRows, newCols);
    }
  };

  // Grid verkleinern (nur wenn keine Schüler betroffen sind)
  const shrinkGrid = (direction: 'right' | 'bottom' | 'both') => {
    let newRows = gridRows;
    let newCols = gridCols;
    
    if (direction === 'right' || direction === 'both') {
      newCols = Math.max(newCols - 1, 4); // Min 4 Spalten
    }
    
    if (direction === 'bottom' || direction === 'both') {
      newRows = Math.max(newRows - 1, 4); // Min 4 Reihen
    }
    
    // Prüfen ob Schüler betroffen wären
    const wouldAffectStudents = gridPositions.some((pos, index) => {
      if (!pos.studentId) return false;
      
      const row = Math.floor(index / gridCols);
      const col = index % gridCols;
      
      return row >= newRows || col >= newCols;
    });
    
    if (!wouldAffectStudents && (newRows !== gridRows || newCols !== gridCols)) {
      handleGridSizeChange(newRows, newCols);
    } else if (wouldAffectStudents) {
      alert('Grid kann nicht verkleinert werden, da Schüler in den betroffenen Bereichen sitzen.');
    }
  };

  // Multiselect-Funktionen
  const toggleStudentSelection = (studentId: string) => {
    if (!isMultiSelectMode) return;
    
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedStudents(new Set());
  };

  const selectAllStudents = () => {
    const allStudentIds = gridPositions
      .filter(pos => pos.studentId)
      .map(pos => pos.studentId!);
    setSelectedStudents(new Set(allStudentIds));
  };

  const moveSelectedStudents = (targetIndex: number) => {
    if (selectedStudents.size === 0) return;
    
    console.log('Moving selected students:', Array.from(selectedStudents), 'to target index:', targetIndex);
    
    const newPositions = [...gridPositions];
    const selectedIds = Array.from(selectedStudents);
    
    // Prüfen ob genügend freie Plätze ab targetIndex verfügbar sind
    let availableSlots = 0;
    for (let i = targetIndex; i < newPositions.length && availableSlots < selectedIds.length; i++) {
      if (!newPositions[i].studentId || selectedStudents.has(newPositions[i].studentId!)) {
        availableSlots++;
      }
    }
    
    // Wenn nicht genügend Plätze verfügbar sind, versuche am Ende des Grids
    if (availableSlots < selectedIds.length) {
      let endSlots = 0;
      for (let i = newPositions.length - 1; i >= 0 && endSlots < selectedIds.length; i--) {
        if (!newPositions[i].studentId || selectedStudents.has(newPositions[i].studentId!)) {
          endSlots++;
        }
      }
      
      if (endSlots >= selectedIds.length) {
        // Setze targetIndex auf das Ende der verfügbaren Plätze
        targetIndex = newPositions.length - endSlots;
      }
    }
    
    // Alle ausgewählten Schüler von ihren Positionen entfernen
    selectedIds.forEach(studentId => {
      const sourceIndex = newPositions.findIndex(pos => pos.studentId === studentId);
      if (sourceIndex !== -1) {
        newPositions[sourceIndex] = { studentId: null, position: sourceIndex, pinned: false };
      }
    });
    
    // Ausgewählte Schüler an neue Positionen setzen (nebeneinander)
    selectedIds.forEach((studentId, index) => {
      const newIndex = targetIndex + index;
      if (newIndex < newPositions.length) {
        newPositions[newIndex] = { studentId, position: newIndex, pinned: false };
      }
    });
    
    setGridPositions(newPositions);
    clearSelection();
  };

  // Lernpartnerschaften-Funktionen
  const handlePartnershipEdit = () => {
    if (selectedStudents.size < 2) {
      toast.error('Es müssen mindestens zwei Schüler ausgewählt werden, um eine Lernpartnerschaft zu definieren');
      return;
    }
    setShowPartnershipModal(true);
  };

  // SICHERER Optimierungsalgorithmus für Sitzordnung
  const optimizeSeatingArrangement = (occupiedPositions: GridPosition[], partnerships: any[], rows: number, cols: number) => {
    if (occupiedPositions.length < 2) return null;

    console.log('=== OPTIMIERUNG STARTET ===');
    console.log('Ursprüngliche Schüler:', occupiedPositions.length, occupiedPositions.map(p => p.studentId));

    // Hilfsfunktionen
    const getPosition = (index: number) => ({ row: Math.floor(index / cols), col: index % cols });
    const getDistance = (pos1: { row: number, col: number }, pos2: { row: number, col: number }) => {
      return Math.sqrt(Math.pow(pos1.row - pos2.row, 2) + Math.pow(pos1.col - pos2.col, 2));
    };

    // Erstelle eine Map der Lernpartnerschaften für schnellen Zugriff
    const partnershipMap = new Map<string, number>();
    partnerships.forEach(partnership => {
      const key1 = `${partnership.schueler1_id}-${partnership.schueler2_id}`;
      const key2 = `${partnership.schueler2_id}-${partnership.schueler1_id}`;
      partnershipMap.set(key1, partnership.gewichtung);
      partnershipMap.set(key2, partnership.gewichtung);
    });

    // Verbesserte Bewertungsfunktion mit detailliertem Logging
    const evaluateArrangement = (positions: { studentId: string, position: number }[], debug = false) => {
      let score = 0;
      let totalPartnerships = 0;
      let positivePartnerships = 0;
      let negativePartnerships = 0;
      
      if (debug) {
        console.log('=== BEWERTUNG DER ANORDNUNG ===');
        console.log('Anzahl Positionen:', positions.length);
        console.log('Verfügbare Partnerschaften:', partnershipMap.size);
      }
      
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const student1 = positions[i];
          const student2 = positions[j];
          const partnershipKey = `${student1.studentId}-${student2.studentId}`;
          const weight = partnershipMap.get(partnershipKey);
          
          if (weight !== undefined) {
            totalPartnerships++;
            const pos1 = getPosition(student1.position);
            const pos2 = getPosition(student2.position);
            const distance = getDistance(pos1, pos2);
            
            let partnershipScore = 0;
            
            if (weight > 0) {
              positivePartnerships++;
              // Positive Partnerschaften: Belohne kurze Distanzen STARK
              if (distance <= 1.0) {
                partnershipScore = weight * 5; // Sehr nah = 5x Belohnung
              } else if (distance <= 1.5) {
                partnershipScore = weight * 3; // Nah = 3x Belohnung
              } else if (distance <= 2.0) {
                partnershipScore = weight * 2; // Mäßig nah = 2x Belohnung
              } else {
                partnershipScore = weight * (1 / distance); // Weitere Distanz = weniger Belohnung
              }
            } else {
              negativePartnerships++;
              // Negative Partnerschaften: Belohne große Distanzen
              const maxDistance = Math.sqrt(Math.pow(rows-1, 2) + Math.pow(cols-1, 2));
              const normalizedDistance = distance / maxDistance;
              partnershipScore = Math.abs(weight) * normalizedDistance * 3;
            }
            
            score += partnershipScore;
            
            if (debug) {
              console.log(`Partnerschaft: ${student1.studentId} ↔ ${student2.studentId} (Gewichtung: ${weight})`);
              console.log(`  Distanz: ${distance.toFixed(2)}, Score: ${partnershipScore.toFixed(2)}`);
            }
          }
        }
      }
      
      if (debug) {
        console.log(`Gesamt-Score: ${score.toFixed(2)}`);
        console.log(`Positive Partnerschaften: ${positivePartnerships}`);
        console.log(`Negative Partnerschaften: ${negativePartnerships}`);
        console.log(`Gesamt Partnerschaften: ${totalPartnerships}`);
      }
      
      return totalPartnerships > 0 ? score / totalPartnerships : 0;
    };

    // NEUER FUNKTIONIERENDER Algorithmus: Greedy + Deterministic
    const optimizeArrangement = () => {
      const normalizeId = (value: any) => {
        if (value === null || value === undefined) return '';
        return String(value).trim();
      };

      const students = occupiedPositions.map(pos => ({
        studentId: normalizeId(pos.studentId),
        position: pos.position,
        pinned: pos.pinned || false
      }));
      const pinnedStudentIds = new Set(
        students
          .filter(student => student.pinned)
          .map(student => normalizeId(student.studentId))
      );
      const availablePositions = occupiedPositions.map(pos => pos.position);
      
      console.log('=== NEUER OPTIMIERUNGSALGORITHMUS ===');
      console.log('Start-Anordnung:', students.length, students.map(s => s.studentId));
      console.log('Verfügbare Positionen:', availablePositions);
      console.log('Verfügbare Partnerschaften:', partnershipMap.size);
      
      // Zeige alle verfügbaren Partnerschaften
      console.log('=== VERFÜGBARE PARTNERSCHAFTEN ===');
      partnerships.forEach(p => {
        const student1 = getStudentById(p.schueler1_id);
        const student2 = getStudentById(p.schueler2_id);
        if (student1 && student2) {
          console.log(`${student1.Vorname} ${student1.Name} ↔ ${student2.Vorname} ${student2.Name} (Gewichtung: ${p.gewichtung})`);
        }
      });
      
      // PHASE 1: Sortiere Partnerschaften nach Gewichtung (stärkste zuerst)
      const sortedPartnerships = partnerships
        .filter(p => Math.abs(p.gewichtung) > 0)
        .sort((a, b) => Math.abs(b.gewichtung) - Math.abs(a.gewichtung));
      
      console.log('=== SORTIERTE PARTNERSCHAFTEN (nach Gewichtung) ===');
      sortedPartnerships.forEach(p => {
        const student1 = getStudentById(p.schueler1_id);
        const student2 = getStudentById(p.schueler2_id);
        if (student1 && student2) {
          console.log(`${student1.Vorname} ${student1.Name} ↔ ${student2.Vorname} ${student2.Name} (Gewichtung: ${p.gewichtung})`);
        }
      });
      
      // PHASE 2: Platziere Partnerschaften deterministisch
      let currentArrangement = [...students];
      let usedPositions = new Set<number>();
      let placedStudentIds = new Set<string>();
      // Tracke die aktuell zugewiesene Position je Schüler (startet mit aktueller Anordnung)
      const assignedPositionByStudent = new Map<string, number>();
      currentArrangement.forEach(s => {
        assignedPositionByStudent.set(normalizeId(s.studentId), s.position);
      });
      
      console.log('=== DETERMINISTISCHE PLATZIERUNG ===');
      
      for (const partnership of sortedPartnerships) {
        const partnershipStudent1Id = normalizeId(partnership.schueler1_id);
        const partnershipStudent2Id = normalizeId(partnership.schueler2_id);
        console.log(`Suche Partnerschaft: ${partnershipStudent1Id} ↔ ${partnershipStudent2Id}`);
        console.log(`Verfügbare Schüler-IDs: ${currentArrangement.map(s => s.studentId)}`);

        const student1Index = currentArrangement.findIndex(s => normalizeId(s.studentId) === partnershipStudent1Id);
        const student2Index = currentArrangement.findIndex(s => normalizeId(s.studentId) === partnershipStudent2Id);
        
        console.log(`Student1 Index: ${student1Index}, Student2 Index: ${student2Index}`);
        
        if (student1Index !== -1 && student2Index !== -1) {
          const student1 = currentArrangement[student1Index];
          const student2 = currentArrangement[student2Index];
          
          console.log(`Platziere Partnerschaft: ${getStudentById(student1.studentId)?.Vorname} ↔ ${getStudentById(student2.studentId)?.Vorname} (Gewichtung: ${partnership.gewichtung})`);
          
          // Finde die beste Platzierung für diese Partnerschaft (respektiere Pinning)
          let bestPos1 = student1.position;
          let bestPos2 = student2.position;
          let bestScore = -Infinity;
          
          // Teste alle möglichen Positionen
          for (const pos1 of availablePositions) {
            for (const pos2 of availablePositions) {
              const s1Id = normalizeId(student1.studentId);
              const s2Id = normalizeId(student2.studentId);
              // Wenn gepinnt, muss die aktuelle Position erhalten bleiben
              const mustKeep1 = Boolean(student1.pinned);
              const mustKeep2 = Boolean(student2.pinned);
              if ((mustKeep1 && pos1 !== assignedPositionByStudent.get(s1Id)) || (mustKeep2 && pos2 !== assignedPositionByStudent.get(s2Id))) {
                continue;
              }
              const prev1 = assignedPositionByStudent.get(s1Id);
              const prev2 = assignedPositionByStudent.get(s2Id);
              const pos1Free = pos1 !== pos2 && (!usedPositions.has(pos1) || prev1 === pos1);
              const pos2Free = pos1 !== pos2 && (!usedPositions.has(pos2) || prev2 === pos2);
              if (pos1Free && pos2Free) {
                const tempArrangement = [...currentArrangement];
                tempArrangement[student1Index] = { ...student1, position: pos1 };
                tempArrangement[student2Index] = { ...student2, position: pos2 };
                
                const score = evaluateArrangement(tempArrangement);
                if (score > bestScore) {
                  bestScore = score;
                  bestPos1 = pos1;
                  bestPos2 = pos2;
                }
              }
            }
          }
          
          // KRITISCH: Setze die beste Platzierung IMMER
          console.log(`  → Beste Positionen gefunden: ${bestPos1} ↔ ${bestPos2}`);
          console.log(`  → Beste Score: ${bestScore.toFixed(2)}`);
          
          // Vorher belegte Positionen freigeben (falls Schüler bereits gesetzt wurden)
          const s1Id = normalizeId(student1.studentId);
          const s2Id = normalizeId(student2.studentId);
          const old1 = assignedPositionByStudent.get(s1Id);
          const old2 = assignedPositionByStudent.get(s2Id);
          // Bei Pinning: Erzwinge, dass bestPos = alte Position
          if (currentArrangement[student1Index].pinned) bestPos1 = old1 ?? bestPos1;
          if (currentArrangement[student2Index].pinned) bestPos2 = old2 ?? bestPos2;
          if (old1 !== undefined && usedPositions.has(old1) && old1 !== bestPos1) usedPositions.delete(old1);
          if (old2 !== undefined && usedPositions.has(old2) && old2 !== bestPos2) usedPositions.delete(old2);

          // Neue Positionen zuweisen und sperren
          currentArrangement[student1Index] = { ...student1, position: bestPos1 };
          currentArrangement[student2Index] = { ...student2, position: bestPos2 };
          assignedPositionByStudent.set(s1Id, bestPos1);
          assignedPositionByStudent.set(s2Id, bestPos2);
          usedPositions.add(bestPos1);
          usedPositions.add(bestPos2);
          placedStudentIds.add(normalizeId(student1.studentId));
          placedStudentIds.add(normalizeId(student2.studentId));
          
          const pos1 = getPosition(bestPos1);
          const pos2 = getPosition(bestPos2);
          const distance = getDistance(pos1, pos2);
          
          console.log(`  → FINALE Positionen: (${pos1.row},${pos1.col}) ↔ (${pos2.row},${pos2.col})`);
          console.log(`  → FINALE Distanz: ${distance.toFixed(2)}`);
          console.log(`  → FINALE Score: ${bestScore.toFixed(2)}`);
          
          // VALIDIERUNG: Prüfe ob die Platzierung tatsächlich erfolgt ist
          const finalStudent1 = currentArrangement[student1Index];
          const finalStudent2 = currentArrangement[student2Index];
          console.log(`  → VALIDIERUNG: Student1 Position: ${finalStudent1.position}, Student2 Position: ${finalStudent2.position}`);
        } else {
          console.log(`FEHLER: Schüler nicht gefunden für Partnerschaft ${partnership.schueler1_id} ↔ ${partnership.schueler2_id}`);
        }
      }
      
      // PHASE 3: Platziere verbleibende Schüler
      // WICHTIG: Nicht nach Position filtern (sonst verlieren wir evtl. Schüler,
      // die bisher auf den neu belegten Positionen saßen). Stattdessen nach IDs.
      const remainingStudents = currentArrangement.filter(s => !placedStudentIds.has(normalizeId(s.studentId)));
      const remainingPositions = availablePositions.filter(pos => !usedPositions.has(pos));

      // Sichere Zuordnung: Falls es mehr Schüler als freie Positionen gibt, fülle zuerst frei,
      // danach belege die nächstbesten Positionen (zur Not überschreibe die am wenigsten gewichteten später)
      if (remainingPositions.length < remainingStudents.length) {
        console.warn('Warnung: Weniger freie Positionen als verbleibende Schüler. Fülle der Reihe nach.');
      }
      
      console.log(`=== PLATZIERE VERBLEIBENDE SCHÜLER ===`);
      console.log(`Verbleibende Schüler: ${remainingStudents.length}`);
      console.log(`Verbleibende Positionen: ${remainingPositions.length}`);
      
      for (let i = 0; i < remainingStudents.length; i++) {
        const st = remainingStudents[i];
        const studentIndex = currentArrangement.findIndex(s => normalizeId(s.studentId) === normalizeId(st.studentId));
        // Suche eine wirklich freie Position (nicht in usedPositions)
        let pos: number | undefined = undefined;
        for (const candidate of availablePositions) {
          if (!usedPositions.has(candidate)) {
            pos = candidate;
            break;
          }
        }
        // Fallback: Falls alle als benutzt markiert sind, wähle die erste Position, die diesem Schüler evtl. schon zugewiesen ist
        if (pos === undefined) {
          pos = assignedPositionByStudent.get(normalizeId(st.studentId)) ?? availablePositions[0];
        }
        usedPositions.add(pos);
        assignedPositionByStudent.set(normalizeId(st.studentId), pos);
        currentArrangement[studentIndex] = { ...st, position: pos };
      }
      
      // RECONCILIATION: Stelle sicher, dass jeder Original-Schüler genau eine Position hat
      const originalIdsOrdered = students.map(s => normalizeId(s.studentId));
      const finalUsedPositions = new Set<number>();
      const finalAssignments: { studentId: string, position: number }[] = [];
      const allPositionsQueue = [...availablePositions];

      const takeNextFreePos = () => {
        for (const p of allPositionsQueue) {
          if (!finalUsedPositions.has(p)) return p;
        }
        // Sollte nicht passieren, aber fallback auf erste Position
        return allPositionsQueue[0];
      };

      for (const id of originalIdsOrdered) {
        let pos = assignedPositionByStudent.get(id);
        if (pos === undefined || finalUsedPositions.has(pos)) {
          pos = takeNextFreePos();
        }
        finalUsedPositions.add(pos);
        finalAssignments.push({ studentId: id, position: pos });
      }

      // FINALE VALIDIERUNG
      console.log('=== FINALE ANORDNUNG ===');
      console.log('Finale Anordnung:', finalAssignments.length, finalAssignments.map(s => s.studentId));

      if (finalAssignments.length !== students.length) {
        console.error('KRITISCHER FEHLER: Schüler verloren gegangen (nach Reconciliation)!');
        return students; // Fallback
      }

      // PHASE 4: Vollautomatische Meta-Optimierung (ohne Nutzer-Tuning)
      const cloneAssignments = (assignments: { studentId: string, position: number }[]) =>
        assignments.map(entry => ({ ...entry }));

      const createSeededRandom = (seedValue: number) => {
        let state = seedValue >>> 0;
        return () => {
          state = (state + 0x6D2B79F5) | 0;
          let t = Math.imul(state ^ (state >>> 15), 1 | state);
          t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
          return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
      };

      const buildDeterministicSeed = () => {
        const base = [
          ...originalIdsOrdered,
          ...finalAssignments.map(entry => `${entry.studentId}:${entry.position}`),
          ...sortedPartnerships.map(p => `${normalizeId(p.schueler1_id)}-${normalizeId(p.schueler2_id)}:${p.gewichtung}`)
        ].join('|');
        let hash = 2166136261;
        for (let i = 0; i < base.length; i++) {
          hash ^= base.charCodeAt(i);
          hash = Math.imul(hash, 16777619);
        }
        return hash >>> 0;
      };

      const localPairSwapRefinement = (
        startAssignments: { studentId: string, position: number }[],
        movableIds: string[],
        maxPasses: number
      ) => {
        let bestLocal = cloneAssignments(startAssignments);
        let bestLocalScore = evaluateArrangement(bestLocal);

        for (let pass = 0; pass < maxPasses; pass++) {
          let improvedInPass = false;

          for (let i = 0; i < movableIds.length; i++) {
            for (let j = i + 1; j < movableIds.length; j++) {
              const idA = movableIds[i];
              const idB = movableIds[j];
              const indexA = bestLocal.findIndex(entry => normalizeId(entry.studentId) === idA);
              const indexB = bestLocal.findIndex(entry => normalizeId(entry.studentId) === idB);
              if (indexA === -1 || indexB === -1) continue;

              const candidate = cloneAssignments(bestLocal);
              const positionA = candidate[indexA].position;
              candidate[indexA].position = candidate[indexB].position;
              candidate[indexB].position = positionA;

              const candidateScore = evaluateArrangement(candidate);
              if (candidateScore > bestLocalScore + 0.0001) {
                bestLocal = candidate;
                bestLocalScore = candidateScore;
                improvedInPass = true;
              }
            }
          }

          if (!improvedInPass) break;
        }

        return { assignments: bestLocal, score: bestLocalScore };
      };

      const shuffleMovablePositions = (
        baseAssignments: { studentId: string, position: number }[],
        movableIds: string[],
        random: () => number
      ) => {
        const candidate = cloneAssignments(baseAssignments);
        const movableIndexes = candidate
          .map((entry, index) => ({ entry, index }))
          .filter(item => movableIds.includes(normalizeId(item.entry.studentId)))
          .map(item => item.index);
        const movablePositions = movableIndexes.map(index => candidate[index].position);

        for (let i = movablePositions.length - 1; i > 0; i--) {
          const j = Math.floor(random() * (i + 1));
          const tmp = movablePositions[i];
          movablePositions[i] = movablePositions[j];
          movablePositions[j] = tmp;
        }

        movableIndexes.forEach((index, idx) => {
          candidate[index].position = movablePositions[idx];
        });

        return candidate;
      };

      const runSimulatedAnnealing = (
        startAssignments: { studentId: string, position: number }[],
        movableIds: string[],
        random: () => number,
        deadlineMs: number
      ) => {
        if (movableIds.length < 2) {
          const score = evaluateArrangement(startAssignments);
          return { assignments: cloneAssignments(startAssignments), score };
        }

        const movableIndexSet = new Set(movableIds);
        const current = cloneAssignments(startAssignments);
        const movableIndexes = current
          .map((entry, index) => ({ entry, index }))
          .filter(item => movableIndexSet.has(normalizeId(item.entry.studentId)))
          .map(item => item.index);

        let currentScore = evaluateArrangement(current);
        let bestAnnealed = cloneAssignments(current);
        let bestAnnealedScore = currentScore;
        let temperature = Math.max(0.3, Math.sqrt(movableIndexes.length) * 0.9);
        let stagnationSteps = 0;

        while (Date.now() < deadlineMs) {
          const idxA = movableIndexes[Math.floor(random() * movableIndexes.length)];
          let idxB = movableIndexes[Math.floor(random() * movableIndexes.length)];
          if (idxA === idxB) continue;

          const swapped = cloneAssignments(current);
          const positionA = swapped[idxA].position;
          swapped[idxA].position = swapped[idxB].position;
          swapped[idxB].position = positionA;

          const swappedScore = evaluateArrangement(swapped);
          const delta = swappedScore - currentScore;
          const accept = delta >= 0 || Math.exp(delta / Math.max(temperature, 0.0001)) > random();

          if (accept) {
            current[idxA].position = swapped[idxA].position;
            current[idxB].position = swapped[idxB].position;
            currentScore = swappedScore;
          }

          if (swappedScore > bestAnnealedScore + 0.0001) {
            bestAnnealed = swapped;
            bestAnnealedScore = swappedScore;
            stagnationSteps = 0;
          } else {
            stagnationSteps++;
          }

          temperature *= 0.9975;
          if (temperature < 0.05) temperature = 0.05;

          // Bei langer Stagnation leichtes Reheat für bessere Exploration
          if (stagnationSteps > 250) {
            temperature = Math.min(1.2, temperature * 1.8);
            stagnationSteps = 0;
          }
        }

        return { assignments: bestAnnealed, score: bestAnnealedScore };
      };

      const movableStudentIds = originalIdsOrdered.filter(id => !pinnedStudentIds.has(id));
      let bestAssignments = cloneAssignments(finalAssignments);
      let bestScore = evaluateArrangement(bestAssignments);
      const studentCount = originalIdsOrdered.length;
      const totalBudgetMs =
        studentCount <= 12 ? 350 :
        studentCount <= 24 ? 650 :
        studentCount <= 36 ? 900 : 1200;
      const restartCount =
        studentCount <= 12 ? 3 :
        studentCount <= 24 ? 4 :
        studentCount <= 36 ? 5 : 6;
      const optimizationDeadline = Date.now() + totalBudgetMs;
      const random = createSeededRandom(buildDeterministicSeed());

      console.log('=== AUTOMATISCHE META-OPTIMIERUNG ===');
      console.log(`Budget: ${totalBudgetMs}ms, Restarts: ${restartCount}, Bewegliche Schüler: ${movableStudentIds.length}`);

      // Start immer mit kurzer deterministischer Verfeinerung
      const initialRefinement = localPairSwapRefinement(bestAssignments, movableStudentIds, 2);
      bestAssignments = initialRefinement.assignments;
      bestScore = initialRefinement.score;

      for (let restart = 0; restart < restartCount && Date.now() < optimizationDeadline; restart++) {
        const remainingRestarts = restartCount - restart;
        const remainingMs = optimizationDeadline - Date.now();
        if (remainingMs <= 20) break;

        const perRestartBudget = Math.max(80, Math.floor(remainingMs / remainingRestarts));
        const annealingDeadline = Date.now() + perRestartBudget;
        const startAssignments = restart === 0
          ? cloneAssignments(bestAssignments)
          : shuffleMovablePositions(bestAssignments, movableStudentIds, random);

        const annealed = runSimulatedAnnealing(startAssignments, movableStudentIds, random, annealingDeadline);
        const refined = localPairSwapRefinement(annealed.assignments, movableStudentIds, 1);

        if (refined.score > bestScore + 0.0001) {
          bestAssignments = refined.assignments;
          bestScore = refined.score;
        }
      }

      console.log(`Finaler Score nach Auto-Optimierung: ${bestScore.toFixed(3)}`);
      return bestAssignments;
    };

    try {
      const result = optimizeArrangement();
      
      // Zusätzliche Validierung
      const originalStudentIds = occupiedPositions.map(pos => pos.studentId).sort();
      const resultStudentIds = result.map(pos => pos.studentId).sort();
      
      if (originalStudentIds.length !== resultStudentIds.length) {
        console.error('FEHLER: Anzahl der Schüler stimmt nicht überein!');
        return null;
      }
      
      if (!originalStudentIds.every((id, index) => id === resultStudentIds[index])) {
        console.error('FEHLER: Nicht alle Schüler sind im Ergebnis enthalten!');
        return null;
      }
      
      console.log('Optimierung erfolgreich abgeschlossen!');
      console.log('Alle Schüler erhalten:', originalStudentIds.length === resultStudentIds.length);
      return result;
    } catch (error) {
      console.error('Fehler beim Optimieren der Sitzordnung:', error);
      return null;
    }
  };

  // TEMPORÄRE DEBUG-FUNKTION: Zeigt Gewichtungsberechnung an
  const showWeightCalculationDebug = (positions: any[], partnerships: any[], rows: number, cols: number) => {
    console.log('=== GEWICHTUNGSBERECHNUNG DEBUG ===');
    
    // Hilfsfunktionen
    const getPosition = (index: number) => ({ row: Math.floor(index / cols), col: index % cols });
    const getDistance = (pos1: { row: number, col: number }, pos2: { row: number, col: number }) => {
      return Math.sqrt(Math.pow(pos1.row - pos2.row, 2) + Math.pow(pos1.col - pos2.col, 2));
    };

    // Erstelle eine Map der Lernpartnerschaften
    const partnershipMap = new Map<string, number>();
    partnerships.forEach(partnership => {
      const key1 = `${partnership.schueler1_id}-${partnership.schueler2_id}`;
      const key2 = `${partnership.schueler2_id}-${partnership.schueler1_id}`;
      partnershipMap.set(key1, partnership.gewichtung);
      partnershipMap.set(key2, partnership.gewichtung);
    });

    let totalScore = 0;
    let partnershipCount = 0;

    console.log('📊 DETAILLIERTE GEWICHTUNGSBERECHNUNG:');
    console.log('='.repeat(80));

    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const student1 = positions[i];
        const student2 = positions[j];
        const partnershipKey = `${student1.studentId}-${student2.studentId}`;
        const weight = partnershipMap.get(partnershipKey);
        
        if (weight !== undefined) {
          partnershipCount++;
          const pos1 = getPosition(student1.position);
          const pos2 = getPosition(student2.position);
          const distance = getDistance(pos1, pos2);
          
          const student1Name = getStudentById(student1.studentId)?.Vorname + ' ' + getStudentById(student1.studentId)?.Name;
          const student2Name = getStudentById(student2.studentId)?.Vorname + ' ' + getStudentById(student2.studentId)?.Name;
          
          let partnershipScore = 0;
          let calculation = '';
          
          if (weight > 0) {
            // Positive Partnerschaften
            if (distance <= 1.0) {
              partnershipScore = weight * 5;
              calculation = `${weight} × 5 = ${partnershipScore} (sehr nah)`;
            } else if (distance <= 1.5) {
              partnershipScore = weight * 3;
              calculation = `${weight} × 3 = ${partnershipScore} (nah)`;
            } else if (distance <= 2.0) {
              partnershipScore = weight * 2;
              calculation = `${weight} × 2 = ${partnershipScore} (mäßig nah)`;
            } else {
              partnershipScore = weight * (1 / distance);
              calculation = `${weight} × (1/${distance.toFixed(2)}) = ${partnershipScore.toFixed(2)} (weit)`;
            }
          } else {
            // Negative Partnerschaften
            const maxDistance = Math.sqrt(Math.pow(rows-1, 2) + Math.pow(cols-1, 2));
            const normalizedDistance = distance / maxDistance;
            partnershipScore = Math.abs(weight) * normalizedDistance * 3;
            calculation = `${Math.abs(weight)} × ${normalizedDistance.toFixed(2)} × 3 = ${partnershipScore.toFixed(2)} (negativ)`;
          }
          
          totalScore += partnershipScore;
          
          console.log(`🔗 ${student1Name} ↔ ${student2Name}`);
          console.log(`   Gewichtung: ${weight}`);
          console.log(`   Positionen: (${pos1.row},${pos1.col}) ↔ (${pos2.row},${pos2.col})`);
          console.log(`   Distanz: ${distance.toFixed(2)}`);
          console.log(`   Berechnung: ${calculation}`);
          console.log(`   Score: ${partnershipScore.toFixed(2)}`);
          console.log('   ' + '-'.repeat(60));
        }
      }
    }
    
    const averageScore = partnershipCount > 0 ? totalScore / partnershipCount : 0;
    
    console.log('='.repeat(80));
    console.log(`📈 ZUSAMMENFASSUNG:`);
    console.log(`   Gesamt-Score: ${totalScore.toFixed(2)}`);
    console.log(`   Durchschnitts-Score: ${averageScore.toFixed(2)}`);
    console.log(`   Anzahl Partnerschaften: ${partnershipCount}`);
    console.log(`   Anzahl Schüler: ${positions.length}`);
    console.log('='.repeat(80));
  };

  // Optimierungsfunktion für Sitzordnung
  const handleOptimizeSeating = () => {
    const occupiedPositions = gridPositions.filter(pos => pos.studentId);
    
    if (occupiedPositions.length < 2) {
      toast.error('Mindestens zwei Schüler müssen im Grid platziert sein, um zu optimieren');
      return;
    }

    if (partnerships.length === 0) {
      toast.error('Keine Lernpartnerschaften verfügbar. Bitte erstellen Sie zuerst Lernpartnerschaften.');
      return;
    }

    // SICHERHEIT: Speichere die ursprüngliche Anordnung als Backup
    const originalGridPositions = [...gridPositions];
    const originalStudentCount = occupiedPositions.length;
    
    console.log('=== OPTIMIERUNG STARTET ===');
    console.log('Ursprüngliche Schüler-Anzahl:', originalStudentCount);
    console.log('Ursprüngliche Schüler-IDs:', occupiedPositions.map(p => p.studentId));

    // Optimierungsalgorithmus ausführen
    const optimizedPositions = optimizeSeatingArrangement(occupiedPositions, partnerships, gridRows, gridCols);
    
    if (optimizedPositions) {
      // KRITISCHE VALIDIERUNG: Alle Schüler müssen noch da sein
      const originalStudentIds = occupiedPositions.map(pos => pos.studentId).sort();
      const newStudentIds = optimizedPositions.map(pos => pos.studentId).sort();
      
      if (originalStudentIds.length !== newStudentIds.length || 
          !originalStudentIds.every((id, index) => id === newStudentIds[index])) {
        console.error('FEHLER: Schüler verloren gegangen!');
        console.log('Original:', originalStudentIds);
        console.log('Neu:', newStudentIds);
        toast.error('Fehler: Schüler sind bei der Optimierung verloren gegangen!');
        return;
      }
      
      // Neue Positionen setzen
      const newGridPositions = [...gridPositions];
      const pinnedByStudentId = new Map(
        occupiedPositions
          .filter(pos => pos.studentId)
          .map(pos => [String(pos.studentId), Boolean(pos.pinned)])
      );
      
      // Alle Positionen leeren
      newGridPositions.forEach((pos, index) => {
        newGridPositions[index] = { studentId: null, position: index, pinned: false };
      });
      
      // Optimierte Positionen setzen
      optimizedPositions.forEach(({ studentId, position }) => {
        if (position < newGridPositions.length) {
          newGridPositions[position] = {
            studentId,
            position,
            pinned: Boolean(pinnedByStudentId.get(String(studentId)))
          };
        }
      });
      
      // FINALE SICHERHEITSPRÜFUNG
      const finalOccupiedPositions = newGridPositions.filter(pos => pos.studentId);
      const finalStudentCount = finalOccupiedPositions.length;
      
      if (finalStudentCount !== originalStudentCount) {
        console.error('KRITISCHER FEHLER: Schüler-Anzahl stimmt nicht überein!');
        console.log('Original:', originalStudentCount);
        console.log('Final:', finalStudentCount);
        toast.error('Kritischer Fehler: Schüler-Anzahl stimmt nicht überein!');
        // Stelle die ursprüngliche Anordnung wieder her
        setGridPositions(originalGridPositions);
        setPinnedStudents(new Set(originalGridPositions.filter(pos => pos.studentId && pos.pinned).map(pos => String(pos.studentId))));
        return;
      }
      
      setGridPositions(newGridPositions);
      setPinnedStudents(new Set(newGridPositions.filter(pos => pos.studentId && pos.pinned).map(pos => String(pos.studentId))));
      
      // Debug-Informationen
      console.log('=== OPTIMIERUNG ERFOLGREICH ABGESCHLOSSEN ===');
      console.log('Anzahl Schüler:', optimizedPositions.length);
      console.log('Anzahl Lernpartnerschaften:', partnerships.length);
      console.log('Grid-Größe:', `${gridRows}x${gridCols}`);
      
      // Zeige Details der Lernpartnerschaften
      partnerships.forEach(p => {
        const student1 = getStudentById(p.schueler1_id);
        const student2 = getStudentById(p.schueler2_id);
        if (student1 && student2) {
          console.log(`Partnerschaft: ${student1.Vorname} ${student1.Name} ↔ ${student2.Vorname} ${student2.Name} (Gewichtung: ${p.gewichtung})`);
        }
      });
      
      // TEMPORÄRE DEBUG-ANZEIGE: Gewichtungsberechnung
      showWeightCalculationDebug(optimizedPositions, partnerships, gridRows, gridCols);
      
      toast.success(`Sitzordnung automatisch optimiert: ${optimizedPositions.length} Schüler neu angeordnet.`);
    } else {
      console.error('Optimierung fehlgeschlagen - Algorithmus gab null zurück');
      toast.error('Optimierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setPinnedStudents(new Set(gridPositions.filter(pos => pos.studentId && pos.pinned).map(pos => String(pos.studentId))));
    }
  };

  const handlePartnershipApply = async () => {
    try {
      const studentIds = Array.from(selectedStudents);
      
      const response = await apiClient.post('/lernpartnerschaften/bulk', {
        student_ids: studentIds,
        gewichtung: partnershipGewichtung,
        halbwertszeit_tage: partnershipHalbwertszeit,
        beschreibung: `Lernpartnerschaft erstellt aus Sitzplan "${currentSitzplan?.name || 'Neuer Sitzplan'}"`
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowPartnershipModal(false);
        clearSelection();
        // Lernpartnerschaften neu laden
        await loadPartnerships();
      }
    } catch (error: any) {
      console.error('Fehler beim Erstellen der Lernpartnerschaften:', error);
      toast.error(error.response?.data?.message || 'Fehler beim Erstellen der Lernpartnerschaften');
    }
  };

  const handlePartnershipCancel = () => {
    setShowPartnershipModal(false);
  };

  // Lernpartnerschaften laden
  const loadPartnerships = async () => {
    try {
      const response = await apiClient.get('/lernpartnerschaften');
      if (response.data.success) {
        setPartnerships(response.data.data || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Lernpartnerschaften:', error);
    }
  };


  // Drag & Drop
  const handleDragStart = (studentId: string) => {
    if (isMultiSelectMode && selectedStudents.has(studentId)) {
      // Multiselect Drag - alle ausgewählten Schüler mitnehmen
      console.log('Starting multiselect drag with', selectedStudents.size, 'selected students');
      setDraggingId('multiselect');
    } else {
      setDraggingId(studentId);
    }
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const handleDragEnter = (index: number) => {
    // Optional: Visuelles Feedback
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    if (!draggingId) return;
    
    if (draggingId === 'multiselect') {
      // Multiselect Drop
      moveSelectedStudents(targetIndex);
    } else {
      // Einzelner Schüler
      const newPositions = [...gridPositions];
      const sourceIndex = newPositions.findIndex(pos => pos.studentId === draggingId);
      
      if (sourceIndex !== -1) {
        // Schüler von alter Position entfernen
        newPositions[sourceIndex] = { studentId: null, position: sourceIndex, pinned: false };
      }
      
      // Schüler an neue Position setzen
      newPositions[targetIndex] = { studentId: draggingId, position: targetIndex, pinned: false };
      
      setGridPositions(newPositions);
    }
    
    setDraggingId(null);
  };

  // Neue Funktion: Schüler aus Tabelle ins Grid ziehen
  const handleDropFromTable = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    if (!draggingId) return;
    
    // Multiselect Drag behandeln
    if (draggingId === 'multiselect') {
      // Multiselect Drop - alle ausgewählten Schüler verschieben
      console.log('Multiselect drop at index:', targetIndex);
      moveSelectedStudents(targetIndex);
      setDraggingId(null);
      return;
    }
    
    // Prüfen ob es ein Schüler aus der Tabelle ist (nicht aus dem Grid)
    const isFromTable = students.some(student => student.sophomorixUnid === draggingId);
    const isAlreadyInGrid = gridPositions.some(pos => pos.studentId === draggingId);
    
    if (isFromTable && !isAlreadyInGrid) {
      // Schüler aus Tabelle ins Grid ziehen
      const newPositions = [...gridPositions];
      
      // Prüfen ob Zielposition bereits besetzt ist
      if (newPositions[targetIndex].studentId) {
        // Zielposition ist besetzt - Schüler zurück in Tabelle
        setDraggingId(null);
        return;
      }
      
      // Schüler an neue Position setzen
      newPositions[targetIndex] = { studentId: draggingId, position: targetIndex, pinned: false };
      setGridPositions(newPositions);
      
      // Schüler aus der Tabelle entfernen (Filterung wird durch useEffect automatisch angewendet)
    } else if (isAlreadyInGrid) {
      // Normale Grid-zu-Grid Bewegung
      handleDrop(e, targetIndex);
      return; // handleDrop setzt bereits draggingId auf null
    }
    
    setDraggingId(null);
  };

  const handleDropOutside = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggingId) return;
    
    if (draggingId === 'multiselect') {
      // Alle ausgewählten Schüler entfernen und zurück in Tabelle
      const newPositions = [...gridPositions];
      selectedStudents.forEach(studentId => {
        const sourceIndex = newPositions.findIndex(pos => pos.studentId === studentId);
        if (sourceIndex !== -1) {
          newPositions[sourceIndex] = { studentId: null, position: sourceIndex, pinned: false };
        }
        // Schüler zurück in Tabelle (Filterung wird durch useEffect automatisch angewendet)
      });
      setGridPositions(newPositions);
      clearSelection();
    } else {
      // Einzelner Schüler
      const newPositions = [...gridPositions];
      const sourceIndex = newPositions.findIndex(pos => pos.studentId === draggingId);
      
      if (sourceIndex !== -1) {
        newPositions[sourceIndex] = { studentId: null, position: sourceIndex, pinned: false };
        setGridPositions(newPositions);
        
        // Schüler zurück in Tabelle (Filterung wird durch useEffect automatisch angewendet)
      }
    }
    
    setDraggingId(null);
  };

  const getStudentById = (id: string | number | null | undefined) => {
    if (id === null || id === undefined) return undefined;
    const normalizedId = String(id);
    
    // OPTIMIERT: Zuerst in Sitzplan-Schülern suchen (haben Fotos)
    const sitzplanStudent = sitzplanStudents.find(
      s => String(s.sophomorixUnid) === normalizedId || String(s.ID) === normalizedId
    );
    if (sitzplanStudent) return sitzplanStudent;
    
    // Fallback: In normalen Schülern suchen
    return students.find(
      s => String(s.sophomorixUnid) === normalizedId || String(s.ID) === normalizedId
    );
  };

  const handleMemoClick = (student: Student) => {
    setSelectedStudentForMemo(student);
    setShowMemoModal(true);
  };

  const handleEmailClick = async (student: Student) => {
    try {
      // Pre-check: sender name available in personal settings
      try {
        const profRes = await api.get('/profile');
        const profile = profRes.data?.data;
        const fn = (profile?.firstName || '').toString().trim();
        const ln = (profile?.lastName || '').toString().trim();
        const dn = (profile?.displayName || '').toString().trim();
        const hasName = Boolean(`${fn} ${ln}`.trim()) || Boolean(dn);
        if (!hasName) {
          setPendingEmailStudent(student);
          setShowPersonalDataModal(true);
          return;
        }
      } catch {
        // ignore; fallback (username) exists
      }

      // Pre-check: user email credentials exist
      const credRes = await api.get('/email/credentials');
      const creds = credRes.data?.data || [];
      const hasEnabled = creds.some((c: any) => c.enabled);
      if (!hasEnabled) {
        setPendingEmailStudent(student);
        setShowEmailCredentialModal(true);
        return;
      }

      // Öffne das Modal - das EmailModal prüft die Eltern-E-Mails selbst
      setSelectedStudentForEmail(student);
      setShowEmailModal(true);
    } catch (error) {
      console.error('Error in handleEmailClick:', error);
      toast.error('E-Mail-Konten konnten nicht geprüft werden');
    }
  };

  // Toggle Pin eines Schülers
  const togglePinStudent = (studentId: string) => {
    // Update gridPositions to reflect pin status
    setGridPositions(prev => prev.map(pos => 
      pos.studentId === studentId 
        ? { ...pos, pinned: !pos.pinned }
        : pos
    ));
    
    // Update pinnedStudents state
    setPinnedStudents(prev => {
      const next = new Set(prev);
      const currentPos = gridPositions.find(pos => pos.studentId === studentId);
      if (currentPos?.pinned) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  // Anwesenheitsmanagement
  const handleAttendanceClick = async (student: Student, newStatus: 'excused' | 'absent') => {
    if (!currentSessionId) {
      toast.error('Keine aktive Session gefunden. Bitte wählen Sie zuerst eine Session im Lernbüro aus.');
      return;
    }

    try {
      const studentId = student.sophomorixUnid;
      
      if (newStatus === 'excused') {
        // Als entschuldigt markieren
        await api.post('/attendance/move-to-sick', {
          studentIds: [studentId]
        });
        
        setAttendanceStatus(prev => ({
          ...prev,
          [studentId]: 'excused'
        }));
        
        toast.success(`${student.Vorname} ${student.Name} als entschuldigt markiert`);
      } else if (newStatus === 'absent') {
        // Als unentschuldigt abwesend markieren - entfernen aus Anwesenheit
        // Zuerst prüfen ob der Schüler überhaupt in der Anwesenheit ist
        const currentStatus = getAttendanceStatus(studentId);
        if (currentStatus === 'present' || currentStatus === 'excused') {
          await api.post('/attendance/remove-from-lernbuero', {
            studentIds: [studentId]
          });
        }
        
        setAttendanceStatus(prev => ({
          ...prev,
          [studentId]: 'absent'
        }));
        
        toast.success(`${student.Vorname} ${student.Name} als unentschuldigt abwesend markiert`);
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Fehler beim Aktualisieren der Anwesenheit');
    }
  };

  // Get attendance status for a student
  const getAttendanceStatus = (studentId: string) => {
    return attendanceStatus[studentId] || null;
  };

  // Load attendance data for current session
  const loadAttendanceData = async () => {
    if (!currentSessionId) return;
    
    try {
      const response = await api.get('/attendance/lists');
      if (response.data.success) {
        const { inLernbuero, sick } = response.data.data;
        
        // Update attendance status
        const newStatus: Record<string, 'present' | 'absent' | 'excused' | null> = {};
        
        // Schüler im Lernbüro (anwesend) - nur die mit der aktuellen Session-ID
        inLernbuero.forEach((student: any) => {
          if (student.SessionID === currentSessionId) {
            newStatus[student.sophomorixUnid] = 'present';
          }
        });
        
        // Schüler entschuldigt/krank
        sick.forEach((student: any) => {
          newStatus[student.sophomorixUnid] = 'excused';
        });
        
        setAttendanceStatus(newStatus);
      }
    } catch (error) {
      console.error('Error loading attendance data:', error);
    }
  };

  // Ausgewählte festpinnen
  const pinSelectedStudents = () => {
    if (selectedStudents.size === 0) return;
    
    // Update gridPositions to reflect pin status
    setGridPositions(prev => prev.map(pos => 
      selectedStudents.has(pos.studentId || '') 
        ? { ...pos, pinned: true }
        : pos
    ));
    
    // Update pinnedStudents state
    setPinnedStudents(prev => {
      const next = new Set(prev);
      selectedStudents.forEach(id => next.add(id));
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Lade Daten…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/60 dark:bg-red-950/40">
        <h3 className="mb-2 font-semibold text-red-800 dark:text-red-200">Fehler</h3>
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  // Sitzplan-Liste View
  const renderSitzplanList = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Sitzpläne</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Verwalten Sie Ihre Sitzordnungen</p>
        </div>
        <Button type="button" onClick={() => handleEditSitzplan()} className="shrink-0 gap-2">
          <LayoutGrid className="h-4 w-4" aria-hidden />
          Neuen Sitzplan
        </Button>
      </div>

      <Tile variant="surface">
        <TileHeader className="px-4 py-3">
          <ListToolbar
            className="mb-0"
            title="Suche & Kontext"
            description="Sitzpläne filtern und sortieren."
            trailing={
              <>
                <label htmlFor="sitzplan-list-search" className="sr-only">
                  Sitzpläne suchen
                </label>
                <input
                  id="sitzplan-list-search"
                  type="search"
                  placeholder="Sitzpläne suchen…"
                  value={listSearchQuery}
                  onChange={(e) => setListSearchQuery(e.target.value)}
                  className="input-field max-w-md min-h-[2.25rem] min-w-[12rem] flex-1 py-2 text-sm"
                  autoComplete="off"
                />
                <select
                  value={listSelectedKlasse}
                  onChange={(e) => setListSelectedKlasse(e.target.value)}
                  className="input-field max-w-xs min-h-[2.25rem] w-full py-2 text-sm sm:w-56"
                >
                  <option value="all">Alle Kontexte</option>
                  {classes
                    .filter((c) => c !== 'alle')
                    .map((klasse) => (
                      <option key={`class-${klasse}`} value={`class:${klasse}`}>
                        Klasse: {klasse}
                      </option>
                    ))}
                  {courses.map((course) => (
                    <option key={`course-${course.id}`} value={`course:${course.id}`}>
                      Kurs: {course.name}
                    </option>
                  ))}
                  {groups.map((group) => (
                    <option key={`group-${group.id}`} value={`group:${group.id}`}>
                      Gruppe: {group.name}
                    </option>
                  ))}
                </select>
                <Button type="button" size="sm" onClick={handleListSearch} aria-label="Suche ausführen">
                  <Search className="h-4 w-4" />
                </Button>
              </>
            }
          />
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Sortierung</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={listSortBy === 'name' ? 'primary' : 'outline'}
              onClick={() => handleListSort('name')}
            >
              Name {listSortBy === 'name' && (listSortOrder === 'ASC' ? '↑' : '↓')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={listSortBy === 'updated_at' ? 'primary' : 'outline'}
              onClick={() => handleListSort('updated_at')}
            >
              Datum {listSortBy === 'updated_at' && (listSortOrder === 'ASC' ? '↑' : '↓')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={listSortBy === 'context_label' ? 'primary' : 'outline'}
              onClick={() => handleListSort('context_label')}
            >
              Kontext {listSortBy === 'context_label' && (listSortOrder === 'ASC' ? '↑' : '↓')}
            </Button>
          </div>
        </TileHeader>

        <TileContent className="px-4 pb-4 pt-0">
          {sitzplaene.length === 0 ? (
            <EmptyState
              title="Keine Sitzpläne"
              description="Legen Sie mit „Neuen Sitzplan“ eine erste Sitzordnung an."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sitzplaene.map((sitzplan) => (
                <Tile key={sitzplan.id} variant="surface">
                  <TileHeader className="flex flex-row items-start justify-between gap-2 border-0 px-4 pb-0 pt-4">
                    <div className="min-w-0 flex-1">
                      <TileTitle as="h3" className="text-base">
                        {sitzplan.name}
                      </TileTitle>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="shrink-0 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(sitzplan.id);
                      }}
                      title={favorites.has(sitzplan.id) ? 'Favorit entfernen' : 'Als Favorit markieren'}
                      aria-label={favorites.has(sitzplan.id) ? 'Favorit entfernen' : 'Als Favorit markieren'}
                    >
                      <Star
                        className={`h-4 w-4 ${favorites.has(sitzplan.id) ? 'fill-amber-400 text-amber-500' : 'text-gray-400 dark:text-gray-500'}`}
                        strokeWidth={2}
                      />
                    </Button>
                  </TileHeader>
                  <TileContent
                    className="cursor-pointer px-4 pt-1 pb-2"
                    onClick={() => handleViewSitzplan(sitzplan)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewSitzplan(sitzplan);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {sitzplan.beschreibung ? (
                      <TileDescription className="line-clamp-2 text-sm">{sitzplan.beschreibung}</TileDescription>
                    ) : null}
                    <div className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <p>
                        Kontext:{' '}
                        {sitzplan.context_label ||
                          (sitzplan.context_type === 'course'
                            ? 'Kurs'
                            : sitzplan.context_type === 'group'
                              ? 'Gruppe'
                              : sitzplan.klasse || 'Unbekannt')}
                      </p>
                      <p>Schüler: {sitzplan.schueler_anzahl || 0}</p>
                      <p>
                        Erstellt:{' '}
                        {sitzplan.created_at ? new Date(sitzplan.created_at).toLocaleDateString('de-DE') : 'Unbekannt'}
                      </p>
                      <p>Urheber: {sitzplan.ersteller_name || 'Unbekannt'}</p>
                      {sitzplan.parent_name ? (
                        <p className="text-primary-600 dark:text-primary-400">Version von: {sitzplan.parent_name}</p>
                      ) : null}
                    </div>
                  </TileContent>
                  <TileFooter className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="flex-1 min-w-[6rem]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewSitzplan(sitzplan);
                      }}
                    >
                      Anzeigen
                    </Button>
                    {canEdit(sitzplan) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        className="flex-1 min-w-[6rem]"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSitzplan(sitzplan);
                        }}
                      >
                        Bearbeiten
                      </Button>
                    ) : canView(sitzplan) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="flex-1 min-w-[6rem]"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSitzplan(sitzplan);
                        }}
                      >
                        Kopieren
                      </Button>
                    ) : null}
                    {canDelete(sitzplan) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        className="shrink-0 px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSitzplan(sitzplan.id, sitzplan.name);
                        }}
                        title="Sitzplan löschen"
                        aria-label="Sitzplan löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="shrink-0 gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportSitzplanAsPDFFromList(sitzplan.id, sitzplan.name);
                      }}
                      title="Sitzplan als PDF exportieren"
                    >
                      <FileDown className="h-4 w-4" />
                      PDF
                    </Button>
                  </TileFooter>
                </Tile>
              ))}
            </div>
          )}

          {listTotalPages > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleListPageChange(listCurrentPage - 1)}
                disabled={listCurrentPage === 1}
                aria-label="Vorherige Seite"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Seite {listCurrentPage} von {listTotalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleListPageChange(listCurrentPage + 1)}
                disabled={listCurrentPage === listTotalPages}
                aria-label="Nächste Seite"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </TileContent>
      </Tile>
    </div>
  );

  // Sitzplan-View (Readonly)
  const renderSitzplanView = () => (
    <div className="flex min-h-screen flex-col space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mb-2 gap-2"
            onClick={() => {
              setCurrentView(previousView);
              if (previousView === 'list') {
                setCurrentSitzplan(null);
              }
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            Zurück
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">{currentSitzplan?.name}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {currentSitzplan?.context_label || currentSitzplan?.klasse || 'Kein Kontext'} •{' '}
            {currentSitzplan?.schueler_anzahl || 0} Schüler
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit(currentSitzplan!) ? (
            <Button type="button" className="gap-2" onClick={() => handleEditSitzplan(currentSitzplan!)}>
              <Copy className="h-4 w-4" />
              Bearbeiten
            </Button>
          ) : canView(currentSitzplan!) ? (
            <Button type="button" variant="secondary" className="gap-2" onClick={() => handleEditSitzplan(currentSitzplan!)}>
              <Copy className="h-4 w-4" />
              Als Kopie bearbeiten
            </Button>
          ) : null}
          <Button type="button" variant="outline" className="gap-2" onClick={() => exportSitzplanAsPDF()}>
            <Download className="h-4 w-4" />
            PDF Export
          </Button>
        </div>
      </div>

      <Tile variant="surface" className="flex flex-col">
        <TileHeader className="flex flex-row items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <LayoutGrid className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
          <TileTitle as="h2" className="text-base">
            Sitzordnung ({gridRows}×{gridCols})
          </TileTitle>
        </TileHeader>

        <TileContent className="flex flex-col px-4 py-4">
          {(() => {
            // Identifiziere leere Zeilen
            const emptyRows = new Set<number>();
            for (let row = 0; row < gridRows; row++) {
              let isEmpty = true;
              for (let col = 0; col < gridCols; col++) {
                const index = row * gridCols + col;
                if (gridPositions[index]?.studentId) {
                  isEmpty = false;
                  break;
                }
              }
              if (isEmpty) {
                emptyRows.add(row);
              }
            }

            // Identifiziere leere Spalten
            const emptyCols = new Set<number>();
            for (let col = 0; col < gridCols; col++) {
              let isEmpty = true;
              for (let row = 0; row < gridRows; row++) {
                const index = row * gridCols + col;
                if (gridPositions[index]?.studentId) {
                  isEmpty = false;
                  break;
                }
              }
              if (isEmpty) {
                emptyCols.add(col);
              }
            }

            // Erstelle Grid-Template-Strings
            const columnSizes = Array.from({ length: gridCols }, (_, col) => 
              emptyCols.has(col) ? '0.5fr' : '1fr'
            ).join(' ');
            
            // Für Zeilen: Verwende auto, damit sie sich an die Kachelhöhe anpassen
            // Leere Zeilen werden automatisch kleiner, da die Spaltenbreite reduziert ist
            // und die Kacheln aspect-ratio: 1 / 1 haben
            const rowSizes = Array.from({ length: gridRows }, (_, row) => 
              'auto'
            ).join(' ');

            return (
              <div 
                className="grid-container mx-auto"
                style={{ 
                  gridTemplateColumns: columnSizes,
                  gridTemplateRows: rowSizes,
                  width: '100%',
                  maxWidth: '100%'
                }}
              >
          {gridPositions.map((position, index) => {
            const student = position.studentId ? getStudentById(position.studentId) : null;
            const currentRow = Math.floor(index / gridCols);
            const currentCol = index % gridCols;
            const isInEmptyRow = emptyRows.has(currentRow);
            const isInEmptyCol = emptyCols.has(currentCol);
            
            // Bestimme die CSS-Klasse basierend auf leeren Zeilen/Spalten
            let aspectClass = 'aspect-square';
            if (isInEmptyRow && isInEmptyCol) {
              aspectClass = 'grid-position-quarter'; // Beide leer: 1/4 Größe
            } else if (isInEmptyRow) {
              aspectClass = 'grid-position-half-height'; // Nur Zeile leer: halbe Höhe
            } else if (isInEmptyCol) {
              aspectClass = 'grid-position-half-width'; // Nur Spalte leer: halbe Breite, Höhe anpassen
            }
            
            return (
              <div
                key={`grid-view-${index}-${position.studentId || 'empty'}-${position.position}`}
                className={`grid-position border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-xs text-center p-1 ${aspectClass}`}
              >
                {student ? (
                  <div className="relative w-full h-full overflow-hidden rounded-lg group">
                    {hasPhoto(studentPhotos[student.sophomorixUnid]) ? (
                      <img 
                        src={createPhotoDataUrl(studentPhotos[student.sophomorixUnid]!)}
                        alt={`${student.Vorname} ${student.Name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-300 to-gray-500 dark:from-gray-600 dark:to-gray-800">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 pb-0.5 backdrop-blur-sm">
                      <div className="truncate text-center text-xs font-medium leading-tight text-white">
                        {student.Vorname}
                      </div>
                    </div>
                    <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <Button
                        type="button"
                        size="xs"
                        variant="ghost"
                        className={clsx(
                          'h-7 min-h-0 bg-white/90 px-1.5 py-0 hover:bg-white dark:bg-gray-900/80 dark:hover:bg-gray-900',
                          getAttendanceStatus(student.sophomorixUnid) === 'excused'
                            ? 'text-orange-600 dark:text-orange-400'
                            : getAttendanceStatus(student.sophomorixUnid) === 'absent'
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-500 dark:text-gray-400'
                        )}
                        title={
                          getAttendanceStatus(student.sophomorixUnid) === 'excused'
                            ? 'Entschuldigt - Klick: Unentschuldigt'
                            : getAttendanceStatus(student.sophomorixUnid) === 'absent'
                              ? 'Unentschuldigt - Klick: Entschuldigt'
                              : 'Anwesenheit - Klick: Entschuldigt'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentStatus = getAttendanceStatus(student.sophomorixUnid);
                          if (currentStatus === null || currentStatus === 'present') {
                            handleAttendanceClick(student, 'excused');
                          } else if (currentStatus === 'excused') {
                            handleAttendanceClick(student, 'absent');
                          } else {
                            handleAttendanceClick(student, 'excused');
                          }
                        }}
                      >
                        {getAttendanceStatus(student.sophomorixUnid) === 'excused' ? (
                          <UserX className="h-3 w-3" />
                        ) : getAttendanceStatus(student.sophomorixUnid) === 'absent' ? (
                          <UserX className="h-3 w-3" />
                        ) : (
                          <UserCheck className="h-3 w-3" />
                        )}
                      </Button>

                      {currentSessionId ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <AbsenceToggle
                            contextType="lernbuero_session"
                            contextId={currentSessionId}
                            studentUnid={student.sophomorixUnid}
                            activeAbsence={activeAbsencesMap[student.sophomorixUnid] ?? null}
                            onStarted={async () => {
                              const response = await fetchActiveAbsences('lernbuero_session', currentSessionId);
                              setActiveAbsencesMap(response?.data || {});
                            }}
                            onEnded={async () => {
                              const response = await fetchActiveAbsences('lernbuero_session', currentSessionId);
                              setActiveAbsencesMap(response?.data || {});
                            }}
                            compact
                            className="rounded-md bg-white/90 hover:bg-white dark:bg-gray-900/80 dark:hover:bg-gray-900"
                          />
                        </div>
                      ) : null}

                      <Button
                        type="button"
                        size="xs"
                        variant="ghost"
                        className="h-7 min-h-0 bg-white/90 px-1.5 py-0 text-primary-700 hover:bg-white dark:bg-gray-900/80 dark:text-primary-300 dark:hover:bg-gray-900"
                        title="E-Mail verfassen"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmailClick(student);
                        }}
                      >
                        <Mail className="h-3 w-3" />
                      </Button>

                      <Button
                        type="button"
                        size="xs"
                        variant="ghost"
                        className={clsx(
                          'h-7 min-h-0 bg-white/90 px-1.5 py-0 hover:bg-white dark:bg-gray-900/80 dark:hover:bg-gray-900',
                          student.Memo ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'
                        )}
                        title={student.Memo ? 'Notiz bearbeiten' : 'Notiz hinzufügen'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemoClick(student);
                        }}
                      >
                        <StickyNote className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500">{index + 1}</span>
                )}
              </div>
            );
          })}
              </div>
            );
          })()}
        </TileContent>
      </Tile>
    </div>
  );

  // Sitzplan-Editor
  const renderSitzplanEditor = () => (
    <div className="flex h-screen flex-col space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mb-2 gap-2"
            onClick={() => {
              setCurrentView(previousView);
              if (previousView === 'list') {
                setCurrentSitzplan(null);
              }
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            Zurück
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
            {currentSitzplan ? `Bearbeite: ${currentSitzplan.name}` : 'Neuer Sitzplan'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Erstellen und bearbeiten Sie die Sitzordnung</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="primary" className="gap-2" onClick={handleSaveSitzplan} disabled={saving}>
            <Save className="h-4 w-4" />
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
          <Button type="button" variant="outline" className="gap-2" onClick={() => exportSitzplanAsPDF()}>
            <Download className="h-4 w-4" />
            PDF Export
          </Button>
        </div>
      </div>

      {/* Editor Content */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="flex min-h-0 flex-col lg:col-span-1">
          <Tile variant="surface" className="flex min-h-0 flex-1 flex-col">
            <TileHeader className="flex flex-row items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
              <Users className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
              <TileTitle as="h2" className="text-base">
                Schülerliste
              </TileTitle>
            </TileHeader>

            <TileContent className="flex min-h-0 flex-1 flex-col space-y-3 px-4 pb-2 pt-2">
              <label htmlFor="sitzordnung-student-search" className="sr-only">
                Schüler suchen
              </label>
              <input
                id="sitzordnung-student-search"
                type="search"
                placeholder="Schüler suchen…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field min-h-[2.25rem] flex-shrink-0 py-2 text-sm"
                autoComplete="off"
              />
              <select
                value={selectedContextType}
                onChange={(e) => {
                  const nextType = e.target.value as SitzplanContextType;
                  setSelectedContextType(nextType);
                  setSelectedClass('alle');
                }}
                className="input-field min-h-[2.25rem] flex-shrink-0 py-2 text-sm"
              >
                <option value="class">Kontext: Klasse</option>
                <option value="course">Kontext: Kurs</option>
                <option value="group">Kontext: Gruppe</option>
              </select>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="input-field min-h-[2.25rem] flex-shrink-0 py-2 text-sm"
              >
                {selectedContextType === 'class' && <option value="alle">Alle Klassen</option>}
                {selectedContextType === 'course' && <option value="alle">Alle Kurse</option>}
                {selectedContextType === 'group' && <option value="alle">Alle Gruppen</option>}
                {selectedContextType === 'class' &&
                  classes
                    .filter((c) => c !== 'alle')
                    .map((klasse) => (
                      <option key={klasse} value={klasse}>
                        {klasse}
                      </option>
                    ))}
                {selectedContextType === 'course' &&
                  courses.map((course) => (
                    <option key={course.id} value={String(course.id)}>
                      {course.name}
                    </option>
                  ))}
                {selectedContextType === 'group' &&
                  groups.map((group) => (
                    <option key={group.id} value={String(group.id)}>
                      {group.name}
                    </option>
                  ))}
              </select>

              <div ref={listContainerRef} onScroll={handleListScroll} className="sitzordnung-student-scroll min-h-0 flex-1 overflow-y-auto">
                <div className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/95">
                  <div className="grid grid-cols-4">
                    <div className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Foto
                    </div>
                    <div className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Name
                    </div>
                    <div className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Vorname
                    </div>
                    <div className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Klasse
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-900">
                  {filteredStudents.length === 0 ? (
                    <EmptyState
                      className="py-8"
                      title="Keine Schüler gefunden"
                      description={searchTerm ? `Filter: „${searchTerm}“` : undefined}
                    />
                  ) : (
                    filteredStudents.map((student) => (
                      <div
                        key={student.sophomorixUnid}
                        className={clsx(
                          'grid h-16 cursor-pointer grid-cols-4 items-center border-b border-gray-100 transition-colors dark:border-gray-800',
                          draggingId === student.sophomorixUnid
                            ? 'border-l-4 border-l-primary-500 bg-primary-50 dark:bg-primary-900/30'
                            : 'bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800/80'
                        )}
                        draggable
                        onDragStart={() => handleDragStart(student.sophomorixUnid)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="flex items-center justify-center">
                          {hasPhoto(studentPhotos[student.sophomorixUnid]) ? (
                            <img
                              src={createPhotoDataUrl(studentPhotos[student.sophomorixUnid]!)}
                              alt={`${student.Vorname} ${student.Name}`}
                              className="h-10 w-10 shrink-0 rounded-full border-2 border-gray-200 object-cover dark:border-gray-600"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML =
                                    '<div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0"><svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                                }
                              }}
                            />
                          ) : (
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                              <Users className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 px-3 py-3 text-sm text-gray-900 dark:text-gray-100">
                          <div className="truncate">{student.Name || ''}</div>
                        </div>
                        <div className="min-w-0 px-3 py-3 text-sm text-gray-900 dark:text-gray-100">
                          <div className="truncate">{student.Vorname || ''}</div>
                        </div>
                        <div className="min-w-0 px-3 py-3 text-sm text-gray-900 dark:text-gray-100">
                          <div className="truncate">{student.Klasse || ''}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/95">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Users className="mr-1 h-3 w-3" />
                        {filteredStudents.length} Schüler {searchTerm && `(Filter: „${searchTerm}“)`}
                      </div>
                      <div className="flex items-center">
                        <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {Object.keys(studentPhotos).length} Fotos geladen
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TileContent>
          </Tile>
        </div>

        <div className="flex min-h-0 flex-col lg:col-span-3">
          <Tile variant="surface" className="flex min-h-0 flex-1 flex-col">
            <TileHeader className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                <LayoutGrid className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                <TileTitle as="h2" className="text-base">
                  Sitzordnung
                </TileTitle>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-400" htmlFor="sitzordnung-grid-rows">
                  Reihen:
                </label>
                <select
                  id="sitzordnung-grid-rows"
                  value={gridRows}
                  onChange={(e) => handleGridSizeChange(parseInt(e.target.value, 10), gridCols)}
                  className="input-field w-auto min-h-[2.25rem] min-w-[4rem] py-1.5 text-sm"
                >
                  {[4, 5, 6, 7, 8, 9, 10].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <label className="text-sm text-gray-600 dark:text-gray-400" htmlFor="sitzordnung-grid-cols">
                  Spalten:
                </label>
                <select
                  id="sitzordnung-grid-cols"
                  value={gridCols}
                  onChange={(e) => handleGridSizeChange(gridRows, parseInt(e.target.value, 10))}
                  className="input-field w-auto min-h-[2.25rem] min-w-[4rem] py-1.5 text-sm"
                >
                  {[4, 5, 6, 7, 8, 9, 10].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>

                <div className="ml-0 flex items-center gap-1 lg:ml-4">
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => expandGrid('right')}
                    disabled={gridCols >= 10}
                    title="Spalte rechts hinzufügen"
                  >
                    +→
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => expandGrid('bottom')}
                    disabled={gridRows >= 10}
                    title="Reihe unten hinzufügen"
                  >
                    +↓
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => expandGrid('both')}
                    disabled={gridRows >= 10 || gridCols >= 10}
                    title="Reihe und Spalte hinzufügen"
                  >
                    +↘
                  </Button>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => shrinkGrid('right')}
                    disabled={gridCols <= 4}
                    title="Rechte Spalte entfernen"
                  >
                    -→
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => shrinkGrid('bottom')}
                    disabled={gridRows <= 4}
                    title="Untere Reihe entfernen"
                  >
                    -↓
                  </Button>
                </div>
              </div>
            </TileHeader>

            <TileContent className="flex min-h-0 flex-1 flex-col px-4 py-3">
              <div className="mb-2 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={isMultiSelectMode ? 'primary' : 'outline'}
                    onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
                  >
                    {isMultiSelectMode ? 'Multiselect: AN' : 'Multiselect: AUS'}
                  </Button>

                  {isMultiSelectMode ? (
                    <>
                      <Button type="button" size="sm" variant="outline" onClick={selectAllStudents}>
                        Alle auswählen
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={clearSelection}>
                        Auswahl löschen
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={handlePartnershipEdit}>
                        Lernpartnerschaft bearbeiten
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={pinSelectedStudents}
                        title="Ausgewählte Schüler festpinnen"
                      >
                        Ausgewählte festpinnen
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleOptimizeSeating}
                        title="Sitzordnung automatisch bestmöglich optimieren"
                      >
                        Optimieren
                      </Button>
                      {selectedStudents.size > 0 ? (
                        <span className="text-xs text-primary-600 dark:text-primary-400">
                          {selectedStudents.size} ausgewählt
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={isReadOnly}
                      onChange={(e) => setIsReadOnly(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
                    />
                    Read-only freigeben
                  </label>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Lernpartnerschaften: {partnerships.length} verfügbar
                  </div>
                </div>
              </div>

              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                {gridPositions.filter((pos) => pos.studentId).length} von {gridRows * gridCols} Plätzen belegt
              </p>

              <div className="relative flex flex-1 items-center justify-center">
                <div
                  className="grid-container mx-auto max-h-[min(70vh,52rem)] w-full max-w-full overflow-hidden"
                  style={{
                    gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                    gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                    aspectRatio: `${gridCols} / ${gridRows}`
                  }}
                >
                  {gridPositions.map((position, index) => {
                    const student = position.studentId ? getStudentById(position.studentId) : null;
                    const isDragging =
                      draggingId === position.studentId ||
                      (draggingId === 'multiselect' && student && selectedStudents.has(student.sophomorixUnid));
                    const isSelected = student && selectedStudents.has(student.sophomorixUnid);

                    return (
                      <div
                        key={`grid-edit-${index}-${position.studentId || 'empty'}-${position.position}`}
                        className={clsx(
                          'grid-position relative flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-1 text-center text-xs transition-all dark:border-gray-600',
                          student ? 'occupied' : 'hover:border-gray-400 dark:hover:border-gray-500',
                          isDragging && 'dragging',
                          isSelected && 'ring-2 ring-primary-500/60 ring-offset-2 ring-offset-white dark:ring-primary-400/70 dark:ring-offset-gray-900',
                          student ? (isMultiSelectMode ? 'cursor-pointer' : 'cursor-grab') : 'cursor-default'
                        )}
                        draggable={!!student}
                        onDragStart={() => student && handleDragStart(student.sophomorixUnid)}
                        onDragEnd={handleDragEnd}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDropFromTable(e, index)}
                        onClick={() => student && isMultiSelectMode && toggleStudentSelection(student.sophomorixUnid)}
                      >
                        {student ? (
                          <div className="relative h-full w-full overflow-hidden rounded-lg">
                            {hasPhoto(studentPhotos[student.sophomorixUnid]) ? (
                              <img
                                src={createPhotoDataUrl(studentPhotos[student.sophomorixUnid]!)}
                                alt={`${student.Vorname} ${student.Name}`}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-300 to-gray-500 dark:from-gray-600 dark:to-gray-800">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                                  <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 pb-0.5 backdrop-blur-sm">
                              <div className="truncate text-center text-xs font-medium leading-tight text-white">
                                {student.Vorname}
                              </div>
                            </div>

                            <div className="absolute right-1 top-1 flex gap-1">
                              <Button
                                type="button"
                                size="xs"
                                variant="ghost"
                                className="h-7 min-h-0 bg-white/90 px-1.5 py-0 hover:bg-white dark:bg-gray-900/80 dark:hover:bg-gray-900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePinStudent(student.sophomorixUnid);
                                }}
                                title={position.pinned ? 'Fixierung aufheben' : 'Schüler fixieren'}
                              >
                                {position.pinned ? (
                                  <Pin className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                                ) : (
                                  <PinOff className="h-3 w-3 text-gray-500" />
                                )}
                              </Button>
                            </div>

                            {isSelected ? (
                              <div className="absolute left-1 top-1 flex h-3 w-3 items-center justify-center rounded-full border border-white bg-primary-500 dark:border-gray-900">
                                <div className="h-1.5 w-1.5 rounded-full bg-white" />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">{index + 1}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                className="mt-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-950/50 dark:text-gray-400"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDropOutside}
              >
                Schüler hier ablegen, um sie zu entfernen
              </div>
            </TileContent>
          </Tile>
        </div>
      </div>
    </div>
  );

  // Lernpartnerschaften Modal
  const renderPartnershipModal = () => (
    <Modal
      isOpen={showPartnershipModal}
      onClose={handlePartnershipCancel}
      title="Lernpartnerschaft erstellen"
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="outline" onClick={handlePartnershipCancel}>
            Abbruch
          </Button>
          <Button type="button" variant="primary" onClick={handlePartnershipApply}>
            Anwenden
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="partnership-gewichtung">
            Gewichtung
          </label>
          <select
            id="partnership-gewichtung"
            value={partnershipGewichtung}
            onChange={(e) => setPartnershipGewichtung(parseInt(e.target.value, 10))}
            className="input-field min-h-[2.75rem] text-sm"
          >
            <option value={-100}>-100% (Sehr unerwünscht)</option>
            <option value={-50}>-50% (Unerwünscht)</option>
            <option value={0}>0% (Neutral)</option>
            <option value={50}>50% (Gewünscht)</option>
            <option value={100}>100% (Sehr gewünscht)</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="partnership-halbwertszeit">
            Halbwertszeit
          </label>
          <select
            id="partnership-halbwertszeit"
            value={partnershipHalbwertszeit}
            onChange={(e) => setPartnershipHalbwertszeit(parseInt(e.target.value, 10))}
            className="input-field min-h-[2.75rem] text-sm"
          >
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={60}>60 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p>Ausgewählte Schüler: {selectedStudents.size}</p>
          <p>Es werden {(selectedStudents.size * (selectedStudents.size - 1)) / 2} Lernpartnerschaften erstellt.</p>
        </div>
      </div>
    </Modal>
  );

  // Haupt-Render-Funktion
  return (
    <div className="space-y-6">
      {currentView === 'list' && renderSitzplanList()}
      {currentView === 'view' && renderSitzplanView()}
      {currentView === 'edit' && renderSitzplanEditor()}
      {selectedStudentForMemo && (
        <MemoModal
          isOpen={showMemoModal}
          onClose={() => { setShowMemoModal(false); setSelectedStudentForMemo(null); }}
          student={{
            Name: selectedStudentForMemo.Name,
            Vorname: selectedStudentForMemo.Vorname,
            Klasse: selectedStudentForMemo.Klasse,
            Unid: selectedStudentForMemo.sophomorixUnid,
            Memo: selectedStudentForMemo.Memo
          }}
        />
      )}
      {selectedStudentForEmail && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => { setShowEmailModal(false); setSelectedStudentForEmail(null); }}
          to={undefined} // Kein 'to' = automatischer Eltern-Versand
          studentName={`${selectedStudentForEmail.Vorname} ${selectedStudentForEmail.Name} (${selectedStudentForEmail.Klasse})`}
          studentUnid={selectedStudentForEmail.sophomorixUnid}
          context="sitzordnung" // Sitzordnung-Kontext für spezifisches Prompt
          initialSubject={`Nachricht zu ${selectedStudentForEmail.Vorname} ${selectedStudentForEmail.Name}`}
          initialBody={`Sehr geehrte Eltern,\n\nbezüglich Ihres Kindes ${selectedStudentForEmail.Vorname} ${selectedStudentForEmail.Name} (${selectedStudentForEmail.Klasse}).\n\nMit freundlichen Grüßen\n${user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.displayName || user?.username || 'Lernrakete System'}`}
        />
      )}
      <PersonalDataRequiredModal
        isOpen={showPersonalDataModal}
        onClose={() => {
          setShowPersonalDataModal(false);
          setPendingEmailStudent(null);
        }}
        onSaved={async () => {
          setShowPersonalDataModal(false);
          const pending = pendingEmailStudent;
          setPendingEmailStudent(null);
          if (pending) await handleEmailClick(pending);
        }}
      />
      <EmailCredentialModal
        isOpen={showEmailCredentialModal}
        onClose={() => {
          setShowEmailCredentialModal(false);
          setPendingEmailStudent(null);
        }}
        credential={null}
        requiredForSending={true}
        onSave={async (credentials) => {
          try {
            const resp = await api.post('/email/credentials', credentials);
            if (resp.data?.success) {
              toast.success('E-Mail-Konto erfolgreich hinzugefügt');
              setShowEmailCredentialModal(false);
              const pending = pendingEmailStudent;
              setPendingEmailStudent(null);
              if (pending) await handleEmailClick(pending);
            } else {
              toast.error(resp.data?.error || 'Fehler beim Speichern des E-Mail-Kontos');
            }
          } catch (e: any) {
            toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Fehler beim Speichern des E-Mail-Kontos');
          }
        }}
      />
      {renderPartnershipModal()}
    </div>
  );
};

export default Sitzordnung;
